import React from "react";
import i18next from "i18next";
import _ from "lodash";
import jsep from "jsep";
import { v4 as uuidv4 } from 'uuid';

import {
  ActivityLevel,
  AttributeType,
  DocumentTypePropertyType,
  FieldDisplayVariant,
  FieldType,
  InnerFieldValue,
  OcrExtractedFieldType,
  Operator,
  statusEngCode,
  ThirdpartyScoreType
} from "./Constants";
import { InduedAllScores } from "./InduedUtils";
import {
  getEsgScoresForRulesAndCalculations,
  ESG_SCORES_THEMES,
  generateEsgScoresTopics,
  getEsgDepths,
} from './DataBlocksUtils';
import {
  getCompanyCategoryOptions,
  getCountries,
  getCurrencies,
} from './Utils';
import { EvAllScores, EVAllScorePerfomance } from "./EcovadisUtils";
import { getPrefixedSecurityRatingScore, SecurityRatingAllScoresEnum, SecurityRatingGlobalScoreMaturityEnum, SecurityRatingPerformanceEnum } from "./SecurityRatingUtils";
import { CreditsafeEnumForRules, getCreditsafeOptionsDependingOnScore } from "./CreditsafeUtils";

export const CALCULATION_VARIABLE_NAME_MAX_LENGTH = 100;
export const CALCULATION_BRANCH_STRING_RESULT_MAX_LENGTH = 100;
export const CALCULATION_DISPLAY_LABEL_FOR_NULL_RESULT_VALUE = () => i18next.t(`calculator.calculationImpossible`);
export const CALCULATION_TODAY_VARIABLE_NAME = 'TODAY';

export const CalculatorContext = React.createContext({
  selectedCalculation: null,
  activities: [],
  countries: [],
  thirdpartyAttributes: [],
  dossierAttributes: [],
  dossierTypes: [],
  documentTypes: [],
  calculatedAttributes: [],
  thirdPartyIndicators: [],
  financialStrengthScores: [],
  financialStrengthRatings: [],
  sourceVariables: [],
  calculations: [],
  sourceData: [],
  showInduedScores: false,
  showTransparencyScore: false,
  showFinancialStrengthScores: false,
  showEsgScores: false,
  showSourceData: false,
  enableTransparencyScore: false,
  showEcovadisScores: false,
  showSecurityRatingScores: false,
  showCreditsafeScores: false,
  contextModels: [],
  collectiveProceedingSteps: []
});

export const CalculationDeclarationContext = React.createContext({
  addCallback: () => { },
  removeCallBack: () => { },
  isValidVariable: () => { },
});

export const VariableContext = React.createContext({
  onVariableChangeCallBack: () => { },
  currentVariable: null,
});

export const TargetFieldContext = React.createContext({
  selectedTargetFieldCategory: () => { },
});


export const getFormDefaultValues = (calculation) => {

  return {
    id: calculation?.id || "",
    creationDate: calculation?.creationDate || "",
    name: calculation?.name || "",
    nature: calculation?.nature || "",
    executionMode: calculation?.executionMode || "",
    description: calculation?.description || "",
    enabled: calculation?.enabled || false,
    resultType: calculation?.resultType || '',
    targetResourceType: calculation?.targetResourceType || '',
    targetResourceKeys: calculation?.targetResourceKeys || [],
    // Variables
    variables: calculation?.variables?.map((variable) => {
      return getVariableDefaultValues(variable);
    }) ||
      [
        getVariableModel(VariableType.CRITERION_ARGUMENT)
      ],

    // Branches
    branches: calculation?.branches?.map((branch) => {
      return getBranchDefaultValues(branch);
    }).sort((a, b) => {
      return (a.default && b.default) ? parseInt(a.id) - parseInt(b.id) : (a.default) ? -1 : 1;
    }) ||
      [
        getDefaultBranchModel(),
        getBranchModel(),
      ],

    user: calculation?.user || "",
  }
};

const getVariableDefaultValues = (variable) => {
  return {
    id: variable?.id,
    type: variable?.type || VariableType.CRITERION_ARGUMENT,
    name: variable?.name,
    targetField: variable?.targetField,
    calculationId: variable?.calculationId,
  };
};

const getBranchDefaultValues = (branch) => {
  return {
    id: branch?.id,
    default: branch?.default,
    resultValue: branch?.resultValue,
    resultColorCode: branch?.resultColorCode,
    flatCriteria: getBranchFlatCriteriaDefaultValues(branch),
    // connectiveOperator: branch?.connectiveOperator,
    // criteriaGroups: branch?.criteriaGroups?.map((group) => {
    //   return getCriteriaGroupDefaultValues(group);
    // }),
    formula: getBranchFormulaDefaultValue(branch)
  };
};

const getBranchFlatCriteriaDefaultValues = (branch) => {
  let flatCriteria = new Set();

  branch?.criteriaGroups?.forEach((group) => {
    getGroupFlatCriteriaDefaultValues(group)?.forEach((criterion) =>
      flatCriteria.add(criterion),
    );
  });

  return Array.from(flatCriteria);
};

const getGroupFlatCriteriaDefaultValues = (group) => {
  let flatCriteria = new Set();

  group?.criteria?.forEach((criterion) =>
    flatCriteria.add(getCriterionDefaultValues(criterion)),
  );
  group?.childGroups?.forEach((group) => {
    getGroupFlatCriteriaDefaultValues(group)?.forEach((criterion) =>
      flatCriteria.add(criterion),
    );
  });

  return Array.from(flatCriteria);
};

/**Currently not used but keep the code*/
// const getCriteriaGroupDefaultValues = (group) => {
//   return {
//     id: group?.id,
//     connectiveOperator: group?.connectiveOperator,
//     criteria: group?.criteria?.map((criterion) => {
//       return getCriterionDefaultValues(criterion);
//     }),
//     childGroups: group?.childGroups?.map((group) => {
//       return getCriteriaGroupDefaultValues(group);
//     }),
//     parentGroupId: group?.parentGroupId,
//     branchId: group?.branchId,
//   };
// };

const getCriterionDefaultValues = (criterion) => {
  return {
    id: criterion?.id,
    variableId: criterion?.variableId,
    variableType: criterion?.variableType,
    variableName: criterion?.variableName,
    operator: criterion?.operator,
    values: criterion?.values,
    groupId: criterion?.groupId,
  };
};

const getBranchFormulaDefaultValue = (branch) => {
  const flatCriteria = getBranchFlatCriteriaDefaultValues(branch);

  return `${branch.criteriaGroups.map(group => getGroupFormulaDefaultValue(group, flatCriteria)).join(` ${branch.connectiveOperator} `)}`;
}

const getGroupFormulaDefaultValue = (group, flatCriteria) => {
  let combinedParts = [];

  const criteriaPart = group.criteria
    .map(criterion => {
      return _.findIndex(flatCriteria, (entry) => entry.id === criterion.id) + 1;
    })
    .join(` ${group.connectiveOperator} `);

  if (criteriaPart) {
    combinedParts.push(criteriaPart);
  }

  const childGroupsPart = group.childGroups
    .map(childGroup => {
      return getGroupFormulaDefaultValue(childGroup, flatCriteria);
    })
    .join(` ${group.connectiveOperator} `);

  if (childGroupsPart) {
    combinedParts.push(childGroupsPart);
  }

  if (!_.isEmpty(group.childGroups) || (_.size(group.criteria) > 1)) {
    return `(${combinedParts.join(` ${group.connectiveOperator} `)})`;
  } else {
    return `${combinedParts.join(` ${group.connectiveOperator} `)}`;
  }
};

export const CalculatorFormSteps = {
  INITIAL: 'INITIAL',
  DECLARATION: 'DECLARATION',
  FINAL: 'FINAL',
};

export const CalculationResultType = {
  STRING: 'STRING',
  BOOLEAN: 'BOOLEAN',
  ARITHMETIC_FORMULA_OUTPUT: 'ARITHMETIC_FORMULA_OUTPUT',
};

export const CalculationNature = {
  CALCULATED_ATTRIBUTE: 'CALCULATED_ATTRIBUTE',
  INDICATOR: 'INDICATOR',
  SOURCE_VARIABLE: 'SOURCE_VARIABLE'
};

export const CalculationTargetResourceType = {
  THIRD_PARTY: "THIRD_PARTY",
  CONTEXT: "CONTEXT",
  DOSSIER: "DOSSIER"
}

export const ExecutionMode = {
  FIRST_MATCHING_BRANCH_RESULT: "FIRST_MATCHING_BRANCH_RESULT",
  SUM_OF_MATCHING_BRANCH_RESULTS: "SUM_OF_MATCHING_BRANCH_RESULTS"
};

export const ExpressionType = {
  LOGICAL: 'LOGICAL',
  ARITHMETIC: 'ARITHMETIC',
};

export const ConnectiveOperatorType = {
  AND: 'AND',
  OR: 'OR',
};

export const ArithmeticOperatorType = {
  ADDITION: '+',
  SUBSTRACTION: '-',
  MULTIPLICATION: '*',
  DIVISION: '/',
  MODULO: '%',
};

export const CalculationBranchFormulaNodeType = {
  CRITERION: 'CRITERION',
  GROUP: 'GROUP',
};

export const getCalculationTargetResourceTypeOptions = () => {
  return [
    {
      value: CalculationTargetResourceType.THIRD_PARTY,
      label: i18next.t(
        `calculator.dialog.create.targetResourceTypeOption.${CalculationTargetResourceType.THIRD_PARTY}`,
      ),
    },
    {
      value: CalculationTargetResourceType.CONTEXT,
      label: i18next.t(
        `calculator.dialog.create.targetResourceTypeOption.${CalculationTargetResourceType.CONTEXT}`,
      ),
    },
    {
      value: CalculationTargetResourceType.DOSSIER,
      label: i18next.t(
        `calculator.dialog.create.targetResourceTypeOption.${CalculationTargetResourceType.DOSSIER}`,
      ),
    }
  ]
}

export const getCalculationNatureOptions = () => {
  return [
    {
      value: CalculationNature.CALCULATED_ATTRIBUTE,
      label: i18next.t(`calculator.dialog.create.natureOption.${CalculationNature.CALCULATED_ATTRIBUTE}`)
    },
    {
      value: CalculationNature.INDICATOR,
      label: i18next.t(`calculator.dialog.create.natureOption.${CalculationNature.INDICATOR}`)
    },
    {
      value: CalculationNature.SOURCE_VARIABLE,
      label: i18next.t(`calculator.dialog.create.natureOption.${CalculationNature.SOURCE_VARIABLE}`)
    }
  ];
};

export const getExecutionModeOptions = () => {
  return [
    {
      value: ExecutionMode.FIRST_MATCHING_BRANCH_RESULT,
      label: i18next.t(`calculator.dialog.create.executionModeOption.${ExecutionMode.FIRST_MATCHING_BRANCH_RESULT}`)
    },
    {
      value: ExecutionMode.SUM_OF_MATCHING_BRANCH_RESULTS,
      label: i18next.t(`calculator.dialog.create.executionModeOption.${ExecutionMode.SUM_OF_MATCHING_BRANCH_RESULTS}`)
    }
  ]
};

export const getCalculationResultTypeOptions = () => {
  return [
    {
      value: CalculationResultType.STRING,
      label: i18next.t(
        `calculator.dialog.create.resultTypeOption.${CalculationResultType.STRING}`,
      ),
    },
    {
      value: CalculationResultType.ARITHMETIC_FORMULA_OUTPUT,
      label: i18next.t(
        `calculator.dialog.create.resultTypeOption.${CalculationResultType.ARITHMETIC_FORMULA_OUTPUT}`,
      ),
    },
  ];
};

export const getDefaultBranchModel = () => {
  return {
    id: '',
    default: true,
    resultValue: '',
    resultColorCode: '',
    criteria: [],
  };
};

// Modèle de données d'une branche
export const getBranchModel = () => {
  let uuid = uuidv4();

  return {
    id: `tmp-${uuid}`,
    default: false,
    resultValue: '',
    resultColorCode: '',
    flatCriteria: [getCriterionModel()],
    criteriaGroups: [getCriteriaGroupModel()],
    formula: '',
  };
};

export const VariableType = {
  CRITERION_ARGUMENT: 'CRITERION_ARGUMENT',
  RESULT_FORMULA_ARGUMENT: 'RESULT_FORMULA_ARGUMENT',
};

export const getVariableModel = (type) => {
  let uuid = uuidv4();

  return {
    id: `tmp-${uuid}`,
    type: type,
    name: '',
    targetField: '',
    calculationId: '',
  };
};

export const getCriteriaGroupModel = () => {
  let uuid = uuidv4();

  return {
    id: `tmp-${uuid}`,
    connectiveOperator: "",
    criteria: [
      getCriterionModel()
    ],
    childGroups: [],
    parentGroupId: '',
    branchId: '',
  };
};

export const getCriterionModel = () => {
  let uuid = uuidv4();

  return {
    id: `tmp-${uuid}`,
    variableId: '',
    variableType: '',
    variableName: '',
    operator: '',
    values: [],
  };
};

export const TargetFieldCategory = {
  THIRD_PARTY: 'THIRD_PARTY',
  DOSSIER: 'DOSSIER',
  DOCUMENT: 'DOCUMENT',
  SOURCE_VARIABLE: 'SOURCE_VARIABLE',
  CONTEXT: 'CONTEXT',
};

export const TargetFieldNature = {
  ENTITY_FIELD: 'ENTITY_FIELD',
  ATTRIBUTE: 'ATTRIBUTE',
  INDICATOR: 'INDICATOR',
  SOURCE_VARIABLE: 'SOURCE_VARIABLE',
  STATUS: 'STATUS',
  SCORE: 'SCORE',
  SOURCE_DATA: 'SOURCE_DATA',
  CONTEXT_ATTRIBUTE: 'CONTEXT_ATTRIBUTE',
  CONTEXT_TIERS_ATTRIBUTE: 'CONTEXT_TIERS_ATTRIBUTE',
  CONTEXT_DECLARANT_ATTRIBUTE: 'CONTEXT_DECLARANT_ATTRIBUTE',
  CONTEXT_RANK: 'CONTEXT_RANK',
  CONTEXT_ADD_DATE: 'CONTEXT_ADD_DATE',
  CONTEXT_END_DATE: 'CONTEXT_END_DATE',
  USER_INSTANCE: 'USER_INSTANCE'
};

export const getTargetFieldCategoryOptions = () => {
  return [
    {
      value: TargetFieldCategory.THIRD_PARTY,
      label: i18next.t(
        `calculator.dialog.create.variableTargetFieldCategory.${TargetFieldCategory.THIRD_PARTY}`,
      ),
    },
    {
      value: TargetFieldCategory.DOSSIER,
      label: i18next.t(
        `calculator.dialog.create.variableTargetFieldCategory.${TargetFieldCategory.DOSSIER}`,
      ),
    },
    {
      value: TargetFieldCategory.DOCUMENT,
      label: i18next.t(
        `calculator.dialog.create.variableTargetFieldCategory.${TargetFieldCategory.DOCUMENT}`,
      ),
    },
    {
      value: TargetFieldCategory.SOURCE_VARIABLE,
      label: i18next.t(
        `calculator.dialog.create.variableTargetFieldCategory.${TargetFieldCategory.SOURCE_VARIABLE}`,
      ),
    },
    {
      value: TargetFieldCategory.CONTEXT,
      label: i18next.t(
        `calculator.dialog.create.variableTargetFieldCategory.${TargetFieldCategory.CONTEXT}`,
      ),
    },
  ];
};

export const getTargetFieldNatureOptions = (
  hasOptionSubscription = () => false,
) => {
  return {
    THIRD_PARTY: [
      {
        value: TargetFieldNature.ENTITY_FIELD,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.THIRD_PARTY}.${TargetFieldNature.ENTITY_FIELD}`,
        ),
      },
      {
        value: TargetFieldNature.ATTRIBUTE,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.THIRD_PARTY}.${TargetFieldNature.ATTRIBUTE}`,
        ),
      },
      {
        value: TargetFieldNature.INDICATOR,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.THIRD_PARTY}.${TargetFieldNature.INDICATOR}`,
        ),
      },
      {
        value: TargetFieldNature.USER_INSTANCE,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.THIRD_PARTY}.${TargetFieldNature.USER_INSTANCE}`
        ),
      },
      ...(hasOptionSubscription('DATA_HUB')
        ? [
          {
            value: TargetFieldNature.SOURCE_DATA,
            label: i18next.t(
              `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.THIRD_PARTY}.${TargetFieldNature.SOURCE_DATA}`,
            ),
          },
        ]
        : []),
    ],

    DOSSIER: [
      {
        value: TargetFieldNature.ENTITY_FIELD,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.DOSSIER}.${TargetFieldNature.ENTITY_FIELD}`,
        ),
      },
      {
        value: TargetFieldNature.ATTRIBUTE,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.DOSSIER}.${TargetFieldNature.ATTRIBUTE}`,
        ),
      },
      {
        value: TargetFieldNature.STATUS,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.DOSSIER}.${TargetFieldNature.STATUS}`,
        ),
      },
    ],

    DOCUMENT: [
      {
        value: TargetFieldNature.ENTITY_FIELD,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.DOCUMENT}.${TargetFieldNature.ENTITY_FIELD}`,
        ),
      },
      {
        value: TargetFieldNature.STATUS,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.DOCUMENT}.${TargetFieldNature.STATUS}`,
        ),
      },
      {
        value: TargetFieldNature.SCORE,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.DOCUMENT}.${TargetFieldNature.SCORE}`,
        ),
      },
    ],
    SOURCE_VARIABLE: [],
    CONTEXT: [
      {
        value: TargetFieldNature.CONTEXT_ATTRIBUTE,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.CONTEXT}.${TargetFieldNature.CONTEXT_ATTRIBUTE}`,
        ),
      },
      {
        value: TargetFieldNature.CONTEXT_TIERS_ATTRIBUTE,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.CONTEXT}.${TargetFieldNature.CONTEXT_TIERS_ATTRIBUTE}`,
        ),
      },
      {
        value: TargetFieldNature.CONTEXT_DECLARANT_ATTRIBUTE,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.CONTEXT}.${TargetFieldNature.CONTEXT_DECLARANT_ATTRIBUTE}`,
        ),
      },
      {
        value: TargetFieldNature.CONTEXT_RANK,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.CONTEXT}.${TargetFieldNature.CONTEXT_RANK}`,
        ),
      },
      {
        value: TargetFieldNature.CONTEXT_ADD_DATE,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.CONTEXT}.${TargetFieldNature.CONTEXT_ADD_DATE}`,
        ),
      },
      {
        value: TargetFieldNature.CONTEXT_END_DATE,
        label: i18next.t(
          `calculator.dialog.create.variableTargetFieldNature.${TargetFieldCategory.CONTEXT}.${TargetFieldNature.CONTEXT_END_DATE}`,
        ),
      },
    ],
  };
};

export const ContextDirectField = [
  TargetFieldNature.CONTEXT_ADD_DATE,
  TargetFieldNature.CONTEXT_END_DATE,
  TargetFieldNature.CONTEXT_RANK,
];

export const getAvailableTargetFieldNatureOptions = (
  targetFieldCategory,
  hasOptionSubscription,
) => {
  const targetFieldCategoryOptions = getTargetFieldNatureOptions(
    hasOptionSubscription,
  )[targetFieldCategory];

  return targetFieldCategoryOptions || [];
};


export const getTargetFieldOptionsForThirdParty = (
  countries = [],
  activities = [],
  attributes = [],
  calculatedAttributes = [],
  indicators = [],
  financialStrengthScores = [],
  financialStrengthRatings = [],
  sourceData = [],
  withInduedScores = false,
  withTransparencyScore = false,
  withFinancialStrengthScores = false,
  withEsgScores = false,
  withSourceData = false,
  enableTransparencyScore = false,
  bNumericOnly = false,
  withEcovadisScores = false,
  withSecurityRatingScores = false,
  withCreditsafeScores = false,
  lang = 'en',
  collectiveProceedingSteps = []
) => {
  attributes = attributes.sort((a, b) => a.label.localeCompare(b.label));
  indicators = indicators.sort((a, b) => a.label.localeCompare(b.label));

  const esgScoresTopics = generateEsgScoresTopics();

  return [
    ...(!bNumericOnly
      ? [
        {
          value: 'name',
          label: i18next.t("calculator.targetOption.thirdParties"),
          type: FieldType.TEXT,
          valueOptions: [],
          fieldNature: TargetFieldNature.ENTITY_FIELD,
        },
        {
          value: 'thirdpartyCode',
          label: i18next.t("calculator.targetOption.thirdPartyCode"),
          type: FieldType.TEXT,
          valueOptions: [],
          fieldNature: TargetFieldNature.ENTITY_FIELD,
        },
        {
          value: 'etab_activity_sic_major',
          label: i18next.t("calculator.targetOption.activitySicMajor"),
          type: FieldType.ACTIVITY,
          valueOptions: activities.filter(
            (activity) => activity.level === ActivityLevel.SIC_MAJOR,
          ),
          fieldNature: TargetFieldNature.ENTITY_FIELD,
          optionsDisplayVariant: FieldDisplayVariant.AUTOCOMPLETE,
        },
        {
          value: 'etab_activity_sic_2',
          label: i18next.t("calculator.targetOption.activitySic", { number: 2 }),
          type: FieldType.ACTIVITY,
          valueOptions: activities.filter(
            (activity) => activity.level === ActivityLevel.SIC_2_DIGITS,
          ),
          fieldNature: TargetFieldNature.ENTITY_FIELD,
          optionsDisplayVariant: FieldDisplayVariant.AUTOCOMPLETE,
        },
        {
          value: 'etab_activity_sic_3',
          label: i18next.t("calculator.targetOption.activitySic", { number: 3 }),
          type: FieldType.ACTIVITY,
          valueOptions: activities.filter(
            (activity) => activity.level === ActivityLevel.SIC_3_DIGITS,
          ),
          fieldNature: TargetFieldNature.ENTITY_FIELD,
          optionsDisplayVariant: FieldDisplayVariant.AUTOCOMPLETE,
        },
        {
          value: 'etab_activity_sic_4',
          label: i18next.t("calculator.targetOption.activitySic", { number: 4 }),
          type: FieldType.ACTIVITY,
          valueOptions: activities.filter(
            (activity) => activity.level === ActivityLevel.SIC_4_DIGITS,
          ),
          fieldNature: TargetFieldNature.ENTITY_FIELD,
          optionsDisplayVariant: FieldDisplayVariant.AUTOCOMPLETE,
        },
        {
          value: 'etab_activity_sic_6',
          label: i18next.t("calculator.targetOption.activitySic", { number: 6 }),
          type: FieldType.ACTIVITY,
          valueOptions: activities.filter(
            (activity) => activity.level === ActivityLevel.SIC_6_DIGITS,
          ),
          fieldNature: TargetFieldNature.ENTITY_FIELD,
          optionsDisplayVariant: FieldDisplayVariant.AUTOCOMPLETE,
        },
        {
          value: 'etab_activity_sic_8',
          label: i18next.t("calculator.targetOption.activitySic", { number: 8 }),
          type: FieldType.ACTIVITY,
          valueOptions: activities.filter(
            (activity) => activity.level === ActivityLevel.SIC_8_DIGITS,
          ),
          fieldNature: TargetFieldNature.ENTITY_FIELD,
          optionsDisplayVariant: FieldDisplayVariant.AUTOCOMPLETE,
        },
        {
          value: 'headquarter',
          label: i18next.t("calculator.targetOption.headquarters"),
          type: FieldType.BOOLEAN,
          valueOptions: [
            {
              value: 'true',
              label: i18next.t("headquarters")
            },
            {
              value: 'false',
              label: i18next.t("secondaryBranch")
            },
          ],
          fieldNature: TargetFieldNature.ENTITY_FIELD,
        },
        {
          value: 'brandName',
          label: i18next.t("calculator.targetOption.brandName"),
          type: FieldType.TEXT,
          valueOptions: [],
          fieldNature: TargetFieldNature.ENTITY_FIELD,
        },
        {
          value: 'addressPostCode',
          label: i18next.t("calculator.targetOption.postalCode"),
          type: FieldType.TEXT,
          valueOptions: [],
          fieldNature: TargetFieldNature.ENTITY_FIELD,
        },
        {
          value: 'addressCity',
          label: i18next.t("calculator.targetOption.city"),
          type: FieldType.TEXT,
          valueOptions: [],
          fieldNature: TargetFieldNature.ENTITY_FIELD,
        },
        {
          value: 'addressCountryCode',
          label: i18next.t("calculator.targetOption.country"),
          type: FieldType.TEXT,
          valueOptions: countries,
          fieldNature: TargetFieldNature.ENTITY_FIELD,
        },
        {
          value: 'legalForm',
          label: 'Forme juridique',
          type: FieldType.TEXT,
          valueOptions: [],
          fieldNature: TargetFieldNature.ENTITY_FIELD,
        },
        {
          value: 'companyCategory',
          label: 'Catégorie d\'entreprise',
          type: FieldType.TEXT,
          valueOptions: [...getCompanyCategoryOptions()],
          fieldNature: TargetFieldNature.ENTITY_FIELD,
        },
        {
          value: 'collectiveProceeding',
          label: i18next.t("calculator.targetOption.collectiveProceeding"),
          type: FieldType.TEXT,
          valueOptions: collectiveProceedingSteps.map((step) => {
            return {
              value: step.code,
              label: i18next.t(`collectiveProceedingStage.${step.code}`)
            }
          }).sort((a, b) => a.label.localeCompare(b.label)),
          fieldNature: TargetFieldNature.ENTITY_FIELD,
        },
        {
          value: 'turnoverCurrency',
          label: 'Chiffre d\'affaires - Devise',
          type: FieldType.TEXT,
          valueOptions: [...getCurrencies()],
          fieldNature: TargetFieldNature.ENTITY_FIELD,
        },
        {
          value: 'withContact',
          label: i18next.t("calculator.targetOption.thirdPartyWithContact"),
          type: FieldType.CUSTOM,
          valueOptions: [
            {
              value: 'true',
              label: i18next.t("yes")
            },
            {
              value: 'false',
              label: i18next.t("no")
            },
          ],
          fieldNature: TargetFieldNature.USER_INSTANCE,
        },
      ]
      : []),

    {
      value: 'workforce',
      label: 'Effectif',
      type: FieldType.NUMERIC,
      valueOptions: [],
      fieldNature: TargetFieldNature.ENTITY_FIELD,
    },
    {
      value: 'shareCapital',
      label: 'Capital',
      type: FieldType.NUMERIC,
      valueOptions: [],
      fieldNature: TargetFieldNature.ENTITY_FIELD,
    },
    {
      value: 'onboardingDate',
      label: 'Date d\'ajout',
      type: FieldType.DATE,
      valueOptions: [],
      fieldNature: TargetFieldNature.ENTITY_FIELD,
    },
    {
      value: 'turnover',
      label: 'Chiffre d\'affaires - Montant',
      type: FieldType.NUMERIC,
      valueOptions: [],
      fieldNature: TargetFieldNature.ENTITY_FIELD,
    },
    ...formatAttributeFieldOptions(attributes, bNumericOnly),

    ...formatCalculatedAttributeFieldOptions(calculatedAttributes, bNumericOnly),

    ...formatIndicatorFieldOptions(indicators, bNumericOnly),

    ...(withInduedScores && !bNumericOnly) ?
      mapTargetFieldForCalculator(InduedAllScores.map(score => {
        return {
          value: score,
          label: i18next.t(`indued.scoresFull.${score}`),
          fieldNature: TargetFieldNature.INDICATOR,
          subFields: mapTargetFieldForCalculator([
            {
              value: InnerFieldValue.VALUE,
              label: i18next.t(`calculator.targetOption.score.value`),
              type: FieldType.RISK,
              fieldNature: TargetFieldNature.INDICATOR,
              path: [score, InnerFieldValue.VALUE],
              subType: ThirdpartyScoreType.INDUED,
              options: [
                {
                  value: '0',
                  label: i18next.t('indued.severity.unknown')
                },
                {
                  value: '1',
                  label: i18next.t('indued.severity.low')
                },
                {
                  value: '2',
                  label: i18next.t('indued.severity.medium')
                },
                {
                  value: '3',
                  label: i18next.t('indued.severity.high')
                },
              ]
            },
            {
              value: InnerFieldValue.LAST_MODIFICATION_DATE,
              label: i18next.t(`calculator.targetOption.score.lastModificationDate`),
              type: FieldType.RISK,
              fieldNature: TargetFieldNature.INDICATOR,
              path: [score, InnerFieldValue.LAST_MODIFICATION_DATE],
              subType: FieldType.DATE,
              options: []
            }
          ])
        }
      }))
      :
      [],
    ...(withTransparencyScore && !bNumericOnly) ?

      mapTargetFieldForCalculator([
        {
          value: 'transparencyScore',
          label: i18next.t('transparency.criterionLabel'),
          fieldNature: TargetFieldNature.INDICATOR,
          disabled: !enableTransparencyScore,
          subFields: mapTargetFieldForCalculator([
            {
              value: InnerFieldValue.VALUE,
              label: i18next.t('calculator.targetOption.score.value'),
              type: FieldType.NUMERIC,
              fieldNature: TargetFieldNature.INDICATOR,
              path: ['transparencyScore', InnerFieldValue.VALUE],
              subType: FieldType.NUMERIC,
              options: []
            },
            {
              value: InnerFieldValue.LAST_MODIFICATION_DATE,
              label: i18next.t('calculator.targetOption.score.lastModificationDate'),
              type: FieldType.NUMERIC,
              fieldNature: TargetFieldNature.INDICATOR,
              path: ['transparencyScore', InnerFieldValue.LAST_MODIFICATION_DATE],
              subType: FieldType.DATE,
              options: []
            }
          ])
        },
      ])
      :
      [],

    ...(withFinancialStrengthScores && !bNumericOnly) ?
      mapTargetFieldForCalculator(
        [
          {
            value: 'financial_strength_score',
            label: i18next.t('financialStrength.criteriaBuilderScoreLabel'),
            fieldNature: TargetFieldNature.INDICATOR,
            subFields: mapTargetFieldForCalculator([
              {
                value: InnerFieldValue.VALUE,
                label: i18next.t(`calculator.targetOption.score.value`),
                type: FieldType.RISK,
                fieldNature: TargetFieldNature.INDICATOR,
                path: ['financial_strength_score', InnerFieldValue.VALUE],
                subType: ThirdpartyScoreType.DATABLOCKS_FINANCIAL_STRENGTH,
                options: financialStrengthScores
              },
              {
                value: InnerFieldValue.LAST_MODIFICATION_DATE,
                label: i18next.t(`calculator.targetOption.score.lastModificationDate`),
                type: FieldType.RISK,
                fieldNature: TargetFieldNature.INDICATOR,
                path: ['financial_strength_score', InnerFieldValue.LAST_MODIFICATION_DATE],
                subType: FieldType.DATE,
                options: []
              }
            ])
          },
          {
            value: 'financial_strength_rating',
            label: i18next.t('financialStrength.criteriaBuilderRatingLabel'),
            fieldNature: TargetFieldNature.INDICATOR,
            subFields: mapTargetFieldForCalculator([
              {
                value: InnerFieldValue.VALUE,
                label: i18next.t(`calculator.targetOption.score.value`),
                type: FieldType.RISK,
                fieldNature: TargetFieldNature.INDICATOR,
                path: ['financial_strength_rating', InnerFieldValue.VALUE],
                subType: ThirdpartyScoreType.DATABLOCKS_FINANCIAL_STRENGTH,
                options: financialStrengthRatings
              },
              {
                value: InnerFieldValue.LAST_MODIFICATION_DATE,
                label: i18next.t(`calculator.targetOption.score.lastModificationDate`),
                type: FieldType.RISK,
                fieldNature: TargetFieldNature.INDICATOR,
                path: ['financial_strength_rating', InnerFieldValue.LAST_MODIFICATION_DATE],
                subType: FieldType.DATE,
                options: []
              }
            ])
          }
        ]
      )
      :
      [],

    ...withSourceData ?
      mapSourceDataTargetFieldForCalculator(sourceData, bNumericOnly, lang) : [],

    ...withEcovadisScores ?
      mapTargetFieldForCalculator(EvAllScores.map(score => {
        return {
          value: `ecovadis_${score}`,
          label: 'Ecovadis ' + i18next.t(`ecovadis.scores.${score}`),
          fieldNature: TargetFieldNature.INDICATOR,
          subFields: mapTargetFieldForCalculator([
            {
              value: InnerFieldValue.VALUE,
              label: i18next.t(`calculator.targetOption.score.value`),
              type: FieldType.RISK,
              fieldNature: TargetFieldNature.INDICATOR,
              path: [`ecovadis_${score}`, InnerFieldValue.VALUE],
              subType: ThirdpartyScoreType.ECOVADIS,
              options: Object.values(EVAllScorePerfomance).map(performance => ({
                value: performance?.toUpperCase(),
                label: i18next.t(`ecovadis.performance.${performance}`)
              }))
            },
            {
              value: InnerFieldValue.LAST_MODIFICATION_DATE,
              label: i18next.t(`calculator.targetOption.score.lastModificationDate`),
              type: FieldType.RISK,
              fieldNature: TargetFieldNature.INDICATOR,
              path: [`ecovadis_${score}`, InnerFieldValue.LAST_MODIFICATION_DATE],
              subType: FieldType.DATE,
              options: []
            }
          ])
        }
      }))
      :
      [],
    ...(withSecurityRatingScores ?
          mapTargetFieldForCalculator(Object.values(SecurityRatingAllScoresEnum).map(score => {
            let options;
            if(score === SecurityRatingAllScoresEnum.GLOBAL_SCORE) {
              options = Object.values(SecurityRatingGlobalScoreMaturityEnum).map(performance => ({
                value: performance?.toUpperCase(),
                label: i18next.t(`securityRating.maturity.${performance.toLowerCase()}`)
              }))
            } else {
              options = Object.values(SecurityRatingPerformanceEnum).map(performance => ({
                value: performance,
                label: performance
              }))
            }

            return {
              value: getPrefixedSecurityRatingScore(score),
              label: "Security Rating " + i18next.t(`securityRating.scores.${score}`),
              fieldNature: TargetFieldNature.INDICATOR,
              subFields: mapTargetFieldForCalculator([
                {
                  value : InnerFieldValue.VALUE,
                  label: i18next.t(`calculator.targetOption.score.value`),
                  type: FieldType.RISK,
                  fieldNature: TargetFieldNature.INDICATOR,
                  path: [`security_rating_${score}`, InnerFieldValue.VALUE],
                  subType: ThirdpartyScoreType.SECURITY_RATING,
                  options: options
                },
                {
                  value : InnerFieldValue.LAST_MODIFICATION_DATE,
                  label: i18next.t(`calculator.targetOption.score.lastModificationDate`),
                  type: FieldType.RISK,
                  fieldNature: TargetFieldNature.INDICATOR,
                  path: [`security_rating_${score}`, InnerFieldValue.LAST_MODIFICATION_DATE],
                  subType: FieldType.DATE,
                  options: []
                }
              ]),
            };
          }))
          :
          []
        ),

    ...(withEsgScores && !bNumericOnly) ?
      mapEsgScoreTargetFieldForCalculator([
        {
          value: "section_esg", subFields: mapEsgScoreTargetFieldForCalculator([
            { value: "esg_score", options: getEsgScoresForRulesAndCalculations(), path: ["section_esg", "esg_score"] },
            { value: "esg_depth", options: getEsgDepths(), path: ["section_esg", "esg_depth"] },
            {
              value: "section_esg_risk_environment", subFields: mapEsgScoreTargetFieldForCalculator([
                {
                  value: "esg_risk_environment",
                  options: getEsgScoresForRulesAndCalculations(),
                  path: ["section_esg", "section_esg_risk_environment", "esg_risk_environment"]
                },
                {
                  value: "section_esg_risk_environment_theme_emissions_climat", subFields: mapEsgScoreTargetFieldForCalculator([
                    {
                      value: "esg_risk_environment_theme_emissions_climat",
                      options: ESG_SCORES_THEMES,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_emissions_climat", "esg_risk_environment_theme_emissions_climat"]
                    },
                    {
                      value: "esg_risk_environment_topic_climatic_risk",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_emissions_climat", "esg_risk_environment_topic_climatic_risk"]
                    },
                    {
                      value: "esg_risk_environment_topic_ghg_emissions",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_emissions_climat", "esg_risk_environment_topic_ghg_emissions"]
                    },
                  ], true)
                },
                {
                  value: "section_esg_risk_environment_theme_natural_ressources", subFields: mapEsgScoreTargetFieldForCalculator([
                    {
                      value: "esg_risk_environment_theme_natural_ressources",
                      options: ESG_SCORES_THEMES,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_natural_ressources", "esg_risk_environment_theme_natural_ressources"]
                    },
                    {
                      value: "esg_risk_environment_topic_energy_management",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_natural_ressources", "esg_risk_environment_theme_natural_ressources"]
                    },
                    {
                      value: "esg_risk_environment_topic_land_use_biodiversity",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_natural_ressources", "esg_risk_environment_topic_land_use_biodiversity"]
                    },
                    {
                      value: "esg_risk_environment_topic_materials_sourcing",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_natural_ressources", "esg_risk_environment_topic_materials_sourcing"]
                    },
                    {
                      value: "esg_risk_environment_topic_pollution_prevention",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_natural_ressources", "esg_risk_environment_topic_pollution_prevention"]
                    },
                    {
                      value: "esg_risk_environment_topic_waste_hazards",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_natural_ressources", "esg_risk_environment_topic_waste_hazards"]
                    },
                    {
                      value: "esg_risk_environment_topic_water_management",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_natural_ressources", "esg_risk_environment_topic_water_management"]
                    }
                  ], true)
                },
                {
                  value: "section_esg_risk_environment_theme_opportunities", subFields: mapEsgScoreTargetFieldForCalculator([
                    {
                      value: "esg_risk_environment_theme_opportunities",
                      options: ESG_SCORES_THEMES,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_opportunities", "esg_risk_environment_theme_opportunities"]
                    },
                    {
                      value: "esg_risk_environment_topic_certifications",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_opportunities", "esg_risk_environment_topic_certifications"]
                    },
                    {
                      value: "esg_risk_environment_topic_opportunities",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_opportunities", "esg_risk_environment_topic_opportunities"]
                    }
                  ], true)
                },
                {
                  value: "section_esg_risk_environment_theme_risks", subFields: mapEsgScoreTargetFieldForCalculator([
                    {
                      value: "esg_risk_environment_theme_risks",
                      options: ESG_SCORES_THEMES,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_risks", "esg_risk_environment_theme_risks"]
                    },
                    {
                      value: "esg_risk_environment_topic_compliance",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_environment", "section_esg_risk_environment_theme_risks", "esg_risk_environment_topic_compliance"]
                    }
                  ], true)
                },
              ])
            },
            {
              value: "section_esg_risk_social", subFields: mapEsgScoreTargetFieldForCalculator([
                {
                  value: "esg_risk_social",
                  options: getEsgScoresForRulesAndCalculations(),
                  path: ["section_esg", "section_esg_risk_social", "esg_risk_social"]
                },
                {
                  value: "section_esg_risk_social_theme_certifications", subFields: mapEsgScoreTargetFieldForCalculator([
                    {
                      value: "esg_risk_social_theme_certifications",
                      options: ESG_SCORES_THEMES,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_certifications", "esg_risk_social_theme_certifications"]
                    },
                    {
                      value: "esg_risk_social_topic_certifications",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_certifications", "esg_risk_social_topic_certifications"]
                    }
                  ], true)
                },
                {
                  value: "section_esg_risk_social_theme_community", subFields: mapEsgScoreTargetFieldForCalculator([
                    {
                      value: "esg_risk_social_theme_community",
                      options: ESG_SCORES_THEMES,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_community", "esg_risk_social_theme_community"]
                    },
                    {
                      value: "esg_risk_social_topic_community_engagement",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_community", "esg_risk_social_topic_community_engagement"]
                    },
                    {
                      value: "esg_risk_social_topic_corporate_philanthropy",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_community", "esg_risk_social_topic_corporate_philanthropy"]
                    }
                  ], true)
                },
                {
                  value: "section_esg_risk_social_theme_customer_engagement", subFields: mapEsgScoreTargetFieldForCalculator([
                    {
                      value: "esg_risk_social_theme_customer_engagement",
                      options: ESG_SCORES_THEMES,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_customer_engagement", "esg_risk_social_theme_customer_engagement"]
                    },
                    {
                      value: "esg_risk_social_topic_data_privacy",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_customer_engagement", "esg_risk_social_topic_data_privacy"]
                    },
                    {
                      value: "esg_risk_social_topic_products_services",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_customer_engagement", "esg_risk_social_topic_products_services"]
                    }
                  ], true)
                },
                {
                  value: "section_esg_risk_social_theme_human_capital", subFields: mapEsgScoreTargetFieldForCalculator([
                    {
                      value: "esg_risk_social_theme_human_capital",
                      options: ESG_SCORES_THEMES,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_human_capital", "esg_risk_social_theme_human_capital"]
                    },
                    {
                      value: "esg_risk_social_topic_diversity_inclusion",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_human_capital", "esg_risk_social_topic_diversity_inclusion"]
                    },
                    {
                      value: "esg_risk_social_topic_health_safety",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_human_capital", "esg_risk_social_topic_health_safety"]
                    },
                    {
                      value: "esg_risk_social_topic_human_rights_abuses",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_human_capital", "esg_risk_social_topic_human_rights_abuses"]
                    },
                    {
                      value: "esg_risk_social_topic_labor_relations",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_human_capital", "esg_risk_social_topic_labor_relations"]
                    },
                    {
                      value: "esg_risk_social_topic_training_education",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_human_capital", "esg_risk_social_topic_training_education"]
                    }
                  ], true)
                },
                {
                  value: "section_esg_risk_social_theme_products_services", subFields: mapEsgScoreTargetFieldForCalculator([
                    {
                      value: "esg_risk_social_theme_products_services",
                      options: ESG_SCORES_THEMES,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_products_services", "esg_risk_social_theme_products_services"]
                    },
                    {
                      value: "esg_risk_social_topic_cyber_risk",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_products_services", "esg_risk_social_topic_cyber_risk"]
                    },
                    {
                      value: "esg_risk_social_topic_product_quality",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_products_services", "esg_risk_social_topic_product_quality"]
                    }
                  ], true)
                },
                {
                  value: "section_esg_risk_social_theme_supplier_engagement", subFields: mapEsgScoreTargetFieldForCalculator([
                    {
                      value: "esg_risk_social_theme_supplier_engagement",
                      options: ESG_SCORES_THEMES,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_supplier_engagement", "esg_risk_social_theme_supplier_engagement"]
                    },
                    {
                      value: "esg_risk_social_topic_supplier_engagement",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_social", "section_esg_risk_social_theme_supplier_engagement", "esg_risk_social_topic_supplier_engagement"]
                    },
                  ], true)
                }
              ])
            },
            {
              value: "section_esg_risk_governance", subFields: mapEsgScoreTargetFieldForCalculator([
                {
                  value: "esg_risk_governance",
                  options: getEsgScoresForRulesAndCalculations(),
                  path: ["section_esg", "section_esg_risk_governance", "esg_risk_governance"]
                },
                {
                  value: "section_esg_risk_governance_theme_business_resilience", subFields: mapEsgScoreTargetFieldForCalculator([
                    {
                      value: "esg_risk_governance_theme_business_resilience",
                      options: ESG_SCORES_THEMES,
                      path: ["section_esg", "section_esg_risk_governance", "section_esg_risk_governance_theme_business_resilience", "esg_risk_governance_theme_business_resilience"]
                    },
                    {
                      value: "esg_risk_governance_topic_business_resilience",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_governance", "section_esg_risk_governance_theme_business_resilience", "esg_risk_governance_topic_business_resilience"]
                    }
                  ], true)
                },
                {
                  value: "section_esg_risk_governance_theme_corporate_behavior", subFields: mapEsgScoreTargetFieldForCalculator([
                    {
                      value: "esg_risk_governance_theme_corporate_behavior",
                      options: ESG_SCORES_THEMES,
                      path: ["section_esg", "section_esg_risk_governance", "section_esg_risk_governance_theme_corporate_behavior", "esg_risk_governance_theme_corporate_behavior"]
                    },
                    {
                      value: "esg_risk_governance_topic_corporate_compliance_behaviors",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_governance", "section_esg_risk_governance_theme_corporate_behavior", "esg_risk_governance_topic_corporate_compliance_behaviors"]
                    },
                    {
                      value: "esg_risk_governance_topic_certifications",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_governance", "section_esg_risk_governance_theme_corporate_behavior", "esg_risk_governance_topic_certifications"]
                    },
                  ], true)
                },
                {
                  value: "section_esg_risk_governance_theme_corporate_governance", subFields: mapEsgScoreTargetFieldForCalculator([
                    {
                      value: "esg_risk_governance_theme_corporate_governance",
                      options: ESG_SCORES_THEMES,
                      path: ["section_esg", "section_esg_risk_governance", "section_esg_risk_governance_theme_corporate_governance", "esg_risk_governance_theme_corporate_governance"]
                    },
                    {
                      value: "esg_risk_governance_topic_board_accountability",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_governance", "section_esg_risk_governance_theme_corporate_governance", "esg_risk_governance_topic_board_accountability"]
                    },
                    {
                      value: "esg_risk_governance_topic_business_ethics",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_governance", "section_esg_risk_governance_theme_corporate_governance", "esg_risk_governance_topic_business_ethics"]
                    },
                    {
                      value: "esg_risk_governance_topic_business_transparency",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_governance", "section_esg_risk_governance_theme_corporate_governance", "esg_risk_governance_topic_business_transparency"]
                    },
                    {
                      value: "esg_risk_governance_topic_shareholder_rights",
                      options: esgScoresTopics,
                      path: ["section_esg", "section_esg_risk_governance", "section_esg_risk_governance_theme_corporate_governance", "esg_risk_governance_topic_shareholder_rights"]
                    }
                  ], true)
                }
              ])
            }
          ])
        },
      ])
      :
      [],
    ...(withCreditsafeScores ?
      mapTargetFieldForCalculator(Object.values(CreditsafeEnumForRules).map(score => {
        if (score === CreditsafeEnumForRules.PAYMENT_BEHAVIOR) {
          return {
            value: "creditsafe_" + score,
            label: "Creditsafe " + i18next.t(`creditsafe.scores.${score}`),
            fieldNature: TargetFieldNature.INDICATOR,
            subFields: mapTargetFieldForCalculator([
              {
                value: InnerFieldValue.VALUE,
                label: i18next.t(`calculator.targetOption.score.value`),
                type: FieldType.RISK,
                fieldNature: TargetFieldNature.INDICATOR,
                path: [`creditsafe_${score}`, InnerFieldValue.VALUE],
                subType: ThirdpartyScoreType.CREDITSAFE_NUMERIC,
                options: []
              },
              {
                value: InnerFieldValue.LAST_MODIFICATION_DATE,
                label: i18next.t(`calculator.targetOption.score.lastModificationDate`),
                type: FieldType.RISK,
                fieldNature: TargetFieldNature.INDICATOR,
                path: [`creditsafe_${score}`, InnerFieldValue.LAST_MODIFICATION_DATE],
                subType: FieldType.DATE,
                options: []
              }
            ]),
          };
        } else {
          let options = getCreditsafeOptionsDependingOnScore(score, false);

          return {
            value: "creditsafe_" + score,
            label: "Creditsafe " + i18next.t(`creditsafe.scores.${score}`),
            fieldNature: TargetFieldNature.INDICATOR,
            subFields: mapTargetFieldForCalculator([
              {
                value: InnerFieldValue.VALUE,
                label: i18next.t(`calculator.targetOption.score.value`),
                type: FieldType.RISK,
                fieldNature: TargetFieldNature.INDICATOR,
                path: [`creditsafe_${score}`, InnerFieldValue.VALUE],
                subType: ThirdpartyScoreType.CREDITSAFE,
                options: options
              },
              {
                value: InnerFieldValue.LAST_MODIFICATION_DATE,
                label: i18next.t(`calculator.targetOption.score.lastModificationDate`),
                type: FieldType.RISK,
                fieldNature: TargetFieldNature.INDICATOR,
                path: [`creditsafe_${score}`, InnerFieldValue.LAST_MODIFICATION_DATE],
                subType: FieldType.DATE,
                options: []
              }
            ]),
          }
        };
      }))
      :
      []
    )
  ];
};

const mapEsgScoreTargetFieldForCalculator = (array, isNumeric = false) => {
  return array.map((item) => {
    // Si l'item a des subFields, on garde la structure existante
    if (item.subFields) {
      return {
        value: item.value,
        label: i18next.t(`ESG.field.${item.value}`),
        type: FieldType.RISK,
        subType: ThirdpartyScoreType.DATABLOCKS_ESG,
        valueOptions: item.options,
        subFields: item.subFields,
        fieldNature: TargetFieldNature.INDICATOR,
        path: item.path,
        isNumeric: isNumeric,
      };
    }

    // Si c'est un champ final (sans subFields), on retourne un objet avec VALUE et LAST_MODIFICATION_DATE
    return {
      value: item.value,
      label: i18next.t(`ESG.field.${item.value}`),
      fieldNature: TargetFieldNature.INDICATOR,
      path: item.path,
      subFields: [
        {
          value: InnerFieldValue.VALUE,
          label: i18next.t(`calculator.targetOption.score.value`),
          type: FieldType.RISK,
          fieldNature: TargetFieldNature.INDICATOR,
          path: [...(item.path || []), InnerFieldValue.VALUE],
          subType: ThirdpartyScoreType.DATABLOCKS_ESG,
          valueOptions: item.options || [],
          isNumeric: isNumeric
        },
        {
          value: InnerFieldValue.LAST_MODIFICATION_DATE,
          label: i18next.t(`calculator.targetOption.score.lastModificationDate`),
          type: FieldType.RISK,
          fieldNature: TargetFieldNature.INDICATOR,
          path: [...(item.path || []), InnerFieldValue.LAST_MODIFICATION_DATE],
          subType: FieldType.DATE,
          valueOptions: []
        }
      ]
    };
  });
};

export const mapSourceDataTargetFieldForCalculator = (array, bNumericOnly, lang) => {
  let result = [];
  for (let [key, value] of Object.entries(array)) {
    result.push({
      value: key,
      label: key,
      type: FieldType.SOURCE_DATA,
      fieldNature: TargetFieldNature.SOURCE_DATA,
      subFields: mapSourceDataOptionTargetFieldForCalculator(key, value, bNumericOnly, lang),
    });
  }
  return result;
};

const mapSourceDataOptionTargetFieldForCalculator = (
  sourceName,
  array,
  bNumericOnly = false,
  lang,
) => {
  return array
    .filter((item) => !bNumericOnly || ['NUMERIC', 'DATE'].includes(item.type))
    .map((item) => {
      return {
        value: item.id + '',
        label:
          item.label[lang] !== undefined ? item.label[lang] : item.label['en'],
        type: FieldType.SOURCE_DATA,
        subType: item.type === 'STRING' ? FieldType.TEXT : item.type,
        valueOptions: [],
        fieldNature: TargetFieldNature.SOURCE_DATA,
        path: [sourceName, item.id + ''],
      };
    });
};

export const mapTargetFieldForCalculator = (array, isNumeric = false) => {
  return array.map(item => {
    return {
      value: item.value,
      label: item.label,
      type: item.type,
      subType: item.subType,
      valueOptions: item.options,
      subFields: item.subFields,
      fieldNature: item.fieldNature,
      path: item.path,
      isNumeric: isNumeric,
      complexListSubField: item.complexListSubField,
      disabled: item.disabled || false
    }
  })
}

export const getFlatSubFieldsOptions = (array) => {
  const newArray = [...array];
  array.forEach((targetFieldOption) => {
    if (
      targetFieldOption.subFields &&
      Array.isArray(targetFieldOption.subFields)
    ) {
      targetFieldOption.subFields.forEach((subField) => {
        if (subField.subFields && Array.isArray(subField.subFields)) {
          newArray.push(subField);
          const subarray = getFlatSubFieldsOptions(subField.subFields);
          subarray.forEach((subsubfield) => {
            newArray.push(subsubfield);
          });
        } else {
          newArray.push(subField);
        }
      });
    }
  });
  return newArray;
};

export const getTargetFieldOptionsForDossier = (
  attributes = [],
  calculatedAttributes = [],
  indicators = [],
  sourceVariables = [],
  bNumericOnly = false,
) => {
  attributes = attributes.sort((a, b) => a.label.localeCompare(b.label));

  return [
    ...(!bNumericOnly
      ? [
        {
          value: 'reference',
          label: i18next.t("calculator.targetOption.reference"),
          type: FieldType.TEXT,
          valueOptions: [],
          fieldNature: TargetFieldNature.ENTITY_FIELD,
        },
      ]
      : []),

    {
      value: 'startDate',
      label: i18next.t("calculator.targetOption.startDate"),
      type: FieldType.DATE,
      valueOptions: [],
      fieldNature: TargetFieldNature.ENTITY_FIELD,
    },
    {
      value: 'endDate',
      label: i18next.t("calculator.targetOption.endDate"),
      type: FieldType.DATE,
      valueOptions: [],
      fieldNature: TargetFieldNature.ENTITY_FIELD,
    },

    ...mapTargetFieldForCalculator([
      {
        value: 'status',
        label: i18next.t("calculator.targetOption.status"),
        subFields: mapTargetFieldForCalculator([
          ...(bNumericOnly ? [] : [{
            value: InnerFieldValue.VALUE,
            label: i18next.t('calculator.targetOption.dossierTypeStatus.value'),
            type: FieldType.TEXT,
            fieldNature: TargetFieldNature.STATUS,
            path: ['status', InnerFieldValue.VALUE],
            options: [
              {
                value: "IN_PROGRESS",
                label: i18next.t(`viewer.status.statusLabelDossier.${statusEngCode.IN_PROGRESS}`)
              },
              {
                value: "ACTION_REQUIRED",
                label: i18next.t(`viewer.status.statusLabelDossier.${statusEngCode.ACTION_REQUIRED}`)
              },
              {
                value: "NOT_VALID",
                label: i18next.t(`viewer.status.statusLabelDossier.${statusEngCode.NOT_VALID}`)
              },
              {
                value: "VALID",
                label: i18next.t(`viewer.status.statusLabelDossier.${statusEngCode.VALID}`)
              },
              {
                value: "MISSING",
                label: i18next.t(`viewer.status.statusLabelDossier.${statusEngCode.MISSING}`)
              }
            ]
          }]),
          {
            value: InnerFieldValue.LAST_MODIFICATION_DATE,
            label: i18next.t('calculator.targetOption.dossierTypeStatus.lastModificationDate'),
            type: FieldType.TEXT,
            fieldNature: TargetFieldNature.ENTITY_FIELD,
            path: ['status', InnerFieldValue.LAST_MODIFICATION_DATE],
            subType: FieldType.DATE,
            options: []
          }
        ])
      },
    ]),

    ...formatAttributeFieldOptions(attributes, bNumericOnly),

    ...formatCalculatedAttributeFieldOptions(calculatedAttributes, bNumericOnly),

    ...formatIndicatorFieldOptions(indicators, bNumericOnly),

    ...formatSourceVariableFieldOptions(sourceVariables, bNumericOnly),
  ];
};

export const getTargetFieldOptionsForDocument = (
  properties = [],
  ocrExtractedFields = [],
  bNumericOnly = false,
) => {
  const countryList = Object.entries(getCountries(i18next.language))
    .map(([code, name]) => ({ value: code, label: name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return [
    ...mapTargetFieldForCalculator([
      {
        value: 'status',
        label: i18next.t("calculator.targetOption.status"),
        subFields: mapTargetFieldForCalculator([
          ...(bNumericOnly ? [] : [{
            value: InnerFieldValue.VALUE,
            label: i18next.t("calculator.targetOption.documentTypeStatus.value"),
            type: FieldType.TEXT,
            fieldNature: TargetFieldNature.STATUS,
            path: ['status', InnerFieldValue.VALUE],
            options: [
              {
                value: "IN_PROGRESS",
                label: i18next.t(`viewer.status.statusLabel.${statusEngCode.IN_PROGRESS}`)
              },
              {
                value: "ACTION_REQUIRED",
                label: i18next.t(`viewer.status.statusLabel.${statusEngCode.ACTION_REQUIRED}`)
              },
              {
                value: "BELOW_EXPECTATION",
                label: i18next.t(`viewer.status.statusLabel.${statusEngCode.BELOW_EXPECTATION}`)
              },
              {
                value: "NOT_VALID",
                label: i18next.t(`viewer.status.statusLabel.${statusEngCode.NOT_VALID}`)
              },
              {
                value: "VALID",
                label: i18next.t(`viewer.status.statusLabel.${statusEngCode.VALID}`)
              },
              {
                value: "MISSING",
                label: i18next.t(`viewer.status.statusLabel.${statusEngCode.MISSING}`)
              }
            ]
          }]),
          {
            value: 'LAST_MODIFICATION_DATE',
            label: i18next.t('calculator.targetOption.documentTypeStatus.lastModificationDate'),
            type: FieldType.TEXT,
            fieldNature: TargetFieldNature.ENTITY_FIELD,
            path: ['status', 'LAST_MODIFICATION_DATE'],
            subType: FieldType.DATE,
            options: []
          }
        ])
      },
    ]),
    ...(!bNumericOnly) ?
      [
        {
          value: 'sisIdScore',
          label: i18next.t("calculator.targetOption.sisIdScore"),
          type: FieldType.TEXT,
          valueOptions: [
            {
              value: 'NO',
              label: i18next.t('sisid.classification.NO'),
            },
            {
              value: 'LOW',
              label: i18next.t('sisid.classification.LOW'),
            },
            {
              value: 'MEDIUM',
              label: i18next.t('sisid.classification.MEDIUM'),
            },
            {
              value: 'HIGH',
              label: i18next.t('sisid.classification.HIGH'),
            },
          ],
          fieldNature: TargetFieldNature.SCORE,
        },
        {
          value: 'trustPairScore',
          label: 'Scores Trustpair',
          type: FieldType.TEXT,
          valueOptions: [
            {
              value: 'favorable',
              label: i18next.t('favorable'),
            },
            {
              value: 'pending',
              label: i18next.t('pending'),
            },
            {
              value: 'unfavorable',
              label: i18next.t('unfavorable'),
            },
            {
              value: 'anomaly',
              label: i18next.t('anomaly'),
            },
            {
              value: 'unevaluated',
              label: i18next.t('unevaluated'),
            },
          ],
          fieldNature: TargetFieldNature.SCORE,
        },
      ]
      : [],

    {
      value: 'submissionDate',
      label: 'Date de dépôt',
      type: FieldType.DATE,
      valueOptions: [],
      fieldNature: TargetFieldNature.ENTITY_FIELD,
    },
    {
      value: 'expirationDate',
      label: 'Date d\'expiration',
      type: FieldType.DATE,
      valueOptions: [],
      fieldNature: TargetFieldNature.ENTITY_FIELD,
    },

    ...properties?.filter((property) => {
      // Supporting a limited list of field types
      return bNumericOnly ?
        [
          DocumentTypePropertyType.MONTANT,
          DocumentTypePropertyType.DECIMAL,
          DocumentTypePropertyType.NOMBRE,
          DocumentTypePropertyType.DEVISE,
          DocumentTypePropertyType.DATE,
        ].includes(property.type)
        :
        [
          DocumentTypePropertyType.CHAINE,
          DocumentTypePropertyType.MULTILIGNES,
          DocumentTypePropertyType.EMAIL,
          DocumentTypePropertyType.TELEPHONE,
          DocumentTypePropertyType.IBAN,
          DocumentTypePropertyType.MONTANT,
          DocumentTypePropertyType.DECIMAL,
          DocumentTypePropertyType.NOMBRE,
          DocumentTypePropertyType.DEVISE,
          DocumentTypePropertyType.DATE,
          DocumentTypePropertyType.RADIO_BUTTON,
          DocumentTypePropertyType.LIST_ITEM,
          DocumentTypePropertyType.CASE_A_COCHER,
          DocumentTypePropertyType.BANK_COUNTRY_CODE,
          DocumentTypePropertyType.LISTE_CHOIX

          //Not managed
          //DocumentTypePropertyType.QUESTION_TEXTAREA,
          //DocumentTypePropertyType.SIGNATURE,
          //DocumentTypePropertyType.TEXTAREA
        ].includes(property.type);
    }).map((property) => {
      switch (property?.type) {
        case DocumentTypePropertyType.BANK_COUNTRY_CODE:
          return {
            value: `${property?.id}`,
            label: i18next.t(`calculator.dialog.create.variableTarget.bankCountryCode`),
            type: FieldType.FORM_FIELD,
            subType: property?.type,
            valueOptions: countryList,
            fieldNature: TargetFieldNature.ENTITY_FIELD,
          }
        case DocumentTypePropertyType.LISTE_CHOIX:
          return  {
            value: `${property?.id}`,
            label: `${property?.label}`,
            type: FieldType.FORM_FIELD,
            fieldNature: TargetFieldNature.ENTITY_FIELD,
            subFields: mapTargetFieldForCalculator(
              property?.valueOptions?.map((valueOption) => ({
                value: valueOption?.value,
                label: valueOption?.label,
                type: FieldType.FORM_FIELD,
                fieldNature: TargetFieldNature.ENTITY_FIELD,
                path: [`${property?.id}`, valueOption?.value],
                subType: `${property?.type}`,
                complexListSubField: {
                  value: valueOption?.value,
                  label: valueOption?.label,
                  type: valueOption?.type
                },
                options: [
                  DocumentTypePropertyType.NOMBRE,
                  DocumentTypePropertyType.DECIMAL,
                  DocumentTypePropertyType.DEVISE,
                  DocumentTypePropertyType.DATE
                ].includes(valueOption?.type)
                  ? []
                  : (valueOption?.options?.map(option => ({
                      value: option?.label,
                      label: option?.label
                    })) || [])
              })) || []
            )
          }
        default:
          return {
            value: `${property?.id}`,
            label: `${property?.label}`,
            type: FieldType.FORM_FIELD,
            subType: property?.type,
            valueOptions: [
              DocumentTypePropertyType.NOMBRE,
              DocumentTypePropertyType.DECIMAL,
              DocumentTypePropertyType.DEVISE,
              DocumentTypePropertyType.DATE
            ].includes(property?.type)
              ? []
              : (property?.valueOptions?.map(option => ({
                  value: option?.label,
                  label: option?.label
                })) || []),
            fieldNature: TargetFieldNature.ENTITY_FIELD,
          };
      }
    }),

    ...ocrExtractedFields?.filter((ocrField) => {
      return bNumericOnly ?
        [
          OcrExtractedFieldType.NUMERIC
        ].includes(ocrField.type)
        :
        [
          OcrExtractedFieldType.TEXT,
          OcrExtractedFieldType.NUMERIC,
          OcrExtractedFieldType.DATE,
          OcrExtractedFieldType.BOOLEAN
        ].includes(ocrField.type);
    }).map((ocrField) => {
      return {
        value: `${ocrField?.id}`,
        label: `${ocrField?.label}`,
        type: FieldType.OCR_EXTRACTED_FIELD,
        subType: ocrField?.type,
        valueOptions: (OcrExtractedFieldType.BOOLEAN === ocrField?.type) ? [
          {
            value: "true",
            label: i18next.t("yes")
          },
          {
            value: "false",
            label: i18next.t("no")
          }
        ] : [],
        fieldNature: TargetFieldNature.ENTITY_FIELD,
      }
    }),

    ...(ocrExtractedFields.length > 0 && !bNumericOnly ? [{
      value: 'receivability',
      label: i18next.t(`calculator.dialog.create.variableTarget.receivability`),
      type: FieldType.OCR_EXTRACTED_FIELD,
      subType: FieldType.BOOLEAN,
      valueOptions: [
        {
          value: 'true',
          label: i18next.t('yes')
        },
        {
          value: 'false',
          label: i18next.t('no'),
          disabled: true
        }
      ],
      fieldNature: TargetFieldNature.ENTITY_FIELD,
    }] : []),
  ];
};

export const getTargetFieldOptionsForSourceVariable = (
  sourceVariables = [],
  bNumericOnly = false,
) => {
  return [
    ...formatSourceVariableFieldOptions(sourceVariables, bNumericOnly),
  ];
};

export const getTargetFieldOptionsForContextDirectField = (
  targetFieldNature,
) => {
  switch (targetFieldNature) {
    case TargetFieldNature.CONTEXT_ADD_DATE:
      return {
        value: 'submissionDate',
        label: 'Date de dépôt',
        type: FieldType.DATE,
        valueOptions: [],
        fieldNature: TargetFieldNature.CONTEXT_ADD_DATE,
      }
    case TargetFieldNature.CONTEXT_END_DATE:
      return {
        value: 'expirationDate',
        label: "Date d'expiration",
        type: FieldType.DATE,
        valueOptions: [],
        fieldNature: TargetFieldNature.CONTEXT_END_DATE,
      }
    case TargetFieldNature.CONTEXT_RANK:
      return {
        value: `rank`,
        label: '',
        type: FieldType.CONTEXT_RANK,
        subType: 'integer',
        valueOptions: [],
        fieldNature: TargetFieldNature.CONTEXT_RANK,
      };
    default:
      return null
  }
};

export const getTargetFieldOptionsForContextModels = (
  contextsModels = [],
  targetFieldData,
  bNumericOnly = false,
) => {
  const selectedContext = contextsModels.find((context) => {
    return context.id === targetFieldData.context_model_id;
  });
  let attributes;
  switch (targetFieldData.target_field_nature) {
    case TargetFieldNature.CONTEXT_ATTRIBUTE:
      attributes = selectedContext.contextAttributes;
      break;
    case TargetFieldNature.CONTEXT_TIERS_ATTRIBUTE:
      attributes = selectedContext.tiersAttributes;
      break;
    case TargetFieldNature.CONTEXT_DECLARANT_ATTRIBUTE:
      attributes = selectedContext.demandeAttributes;
      break;
    default:
      attributes = []
  }
  return mapTargetFieldForCalculator(attributes.map((attr) => {
    return {
      value: `${attr.id}`,
      label: attr.label,
      type: FieldType.ATTRIBUTE,
      subType: attr.type,
      dt: true,
      fieldNature: targetFieldData.target_field_nature,
      subFields: mapTargetFieldForCalculator([
        ...(bNumericOnly ? [] : [{
          value: InnerFieldValue.VALUE,
          label: i18next.t(`calculator.targetOption.thirdPartyAttributes.value`),
          type: FieldType.ATTRIBUTE,
          fieldNature: targetFieldData.target_field_nature,
          path: [`${attr.id}`, InnerFieldValue.VALUE],
          subType: attr.type,
          dt: true,
          /*
            A boolean attribute doesn't have predefined value options on the backend side,
            but it's a primitive type that has limited options (true and false), so process
            it differently
          */
          options: (AttributeType.BOOLEAN === attr.type) ? [
            {
              value: "true",
              label: i18next.t("yes")
            },
            {
              value: "false",
              label: i18next.t("no")
            },
          ] : attr.values?.map((valueEntry) => {
            return {
              value: valueEntry.value,
              label: valueEntry.value
            }
          }) || []
        }]),
        {
          value: InnerFieldValue.LAST_MODIFICATION_DATE,
          label: i18next.t(`calculator.targetOption.thirdPartyAttributes.lastModificationDate`),
          type: FieldType.ATTRIBUTE,
          fieldNature: targetFieldData.target_field_nature,
          path: [`${attr.id}`, InnerFieldValue.LAST_MODIFICATION_DATE],
          subType: FieldType.DATE,
          options: []
        }
      ])
    }
  }));
};

const formatAttributeFieldOptions = (attributes = [], bNumericOnly = false) => {
  return mapTargetFieldForCalculator(attributes?.filter(attr => ![AttributeType.STRING].includes(attr.type)).map((attr) => {
    return {
      value: `${attr.id}`,
      label: attr.label,
      fieldNature: TargetFieldNature.ATTRIBUTE,
      subFields: mapTargetFieldForCalculator([
        ...((bNumericOnly ?
          [
            AttributeType.DATE,
            AttributeType.DECIMAL,
            AttributeType.INTEGER,
            AttributeType.PERCENTAGE
          ].includes(attr.type)
          :
          true
        ) ? [{
          value: InnerFieldValue.VALUE,
          label: i18next.t(`calculator.targetOption.thirdPartyAttributes.value`),
          type: FieldType.ATTRIBUTE,
          fieldNature: TargetFieldNature.ATTRIBUTE,
          path: [`${attr.id}`, InnerFieldValue.VALUE],
          subType: attr.type,
          dt: true,
          /*
            A boolean attribute doesn't have predefined value options on the backend side,
            but it's a primitive type that has limited options (true and false), so process
            it differently
          */
          options: (AttributeType.BOOLEAN === attr.type) ? [
            {
              value: "true",
              label: i18next.t("yes")
            },
            {
              value: "false",
              label: i18next.t("no")
            },
          ] : attr.values?.map((valueEntry) => {
            return {
              value: valueEntry.value,
              label: valueEntry.value
            }
          }) || []
        }] : []),
        {
          value: InnerFieldValue.LAST_MODIFICATION_DATE,
          label: i18next.t(`calculator.targetOption.thirdPartyAttributes.lastModificationDate`),
          type: FieldType.ATTRIBUTE,
          fieldNature: TargetFieldNature.ATTRIBUTE,
          path: [`${attr.id}`, InnerFieldValue.LAST_MODIFICATION_DATE],
          subType: FieldType.DATE,
          options: []
        }
      ])
    }
  }))
}

const formatCalculatedAttributeFieldOptions = (calculatedAttributes = [], bNumericOnly = false) => {
  return mapTargetFieldForCalculator(calculatedAttributes?.map((attr) => {
    return {
      value: `${attr.value}`,
      label: attr.label,
      fieldNature: TargetFieldNature.ATTRIBUTE,
      subFields: mapTargetFieldForCalculator([
        ...((bNumericOnly ?
          [
            CalculationResultType.ARITHMETIC_FORMULA_OUTPUT
          ].includes(attr.type)
          :
          true
        ) ?
          [{
            value: InnerFieldValue.VALUE,
            label: i18next.t(`calculator.targetOption.thirdPartyAttributes.value`),
            type: FieldType.CALCULATED_ATTRIBUTE,
            fieldNature: TargetFieldNature.ATTRIBUTE,
            path: [`${attr.value}`, InnerFieldValue.VALUE],
            subType: attr.type,
            dt: true,
            options: attr.options?.map((opt) => ({ value: opt, label: opt }))
          }] : []),
        {
          value: InnerFieldValue.LAST_MODIFICATION_DATE,
          label: i18next.t(`calculator.targetOption.thirdPartyAttributes.lastModificationDate`),
          type: FieldType.CALCULATED_ATTRIBUTE,
          fieldNature: TargetFieldNature.ATTRIBUTE,
          path: [`${attr.value}`, InnerFieldValue.LAST_MODIFICATION_DATE],
          subType: FieldType.DATE,
          options: []
        }
      ])
    }
  }));

}

const formatIndicatorFieldOptions = (indicators = [], bNumericOnly = false) => {
  return mapTargetFieldForCalculator(indicators?.map((indicator) => {
    return {
      value: `${indicator.value}`,
      label: indicator.label,
      fieldNature: TargetFieldNature.INDICATOR,
      subFields: mapTargetFieldForCalculator([
        ...((bNumericOnly ?
          [
            CalculationResultType.ARITHMETIC_FORMULA_OUTPUT
          ].includes(indicator.type)
          :
          true
        ) ? [{
          value: InnerFieldValue.VALUE,
          label: i18next.t(`calculator.targetOption.thirdPartyIndicators.value`),
          type: FieldType.INDICATOR,
          fieldNature: TargetFieldNature.INDICATOR,
          path: [`${indicator.value}`, InnerFieldValue.VALUE],
          subType: indicator.type,
          dt: true,
          options: indicator.options?.map((opt) => ({ value: opt, label: opt }))
        }] : []),
        {
          value: InnerFieldValue.LAST_MODIFICATION_DATE,
          label: i18next.t(`calculator.targetOption.thirdPartyIndicators.lastModificationDate`),
          type: FieldType.INDICATOR,
          fieldNature: TargetFieldNature.INDICATOR,
          path: [`${indicator.value}`, InnerFieldValue.LAST_MODIFICATION_DATE],
          subType: FieldType.DATE,
          options: []
        }
      ])
    }
  }));
}

const formatSourceVariableFieldOptions = (sourceVariables = [], bNumericOnly = false) => {
  return mapTargetFieldForCalculator(sourceVariables)?.map((sourceVariable) => {
    return {
      value: `${sourceVariable.value}`,
      label: sourceVariable.label,
      fieldNature: TargetFieldNature.SOURCE_VARIABLE,
      subFields: mapTargetFieldForCalculator([
        ...((bNumericOnly ?
          [
            CalculationResultType.ARITHMETIC_FORMULA_OUTPUT
          ].includes(sourceVariable.type)
          :
          true
        ) ? [{
          value: InnerFieldValue.VALUE,
          label: i18next.t(`calculator.targetOption.sourceVariables.value`),
          type: FieldType.SOURCE_VARIABLE,
          fieldNature: TargetFieldNature.SOURCE_VARIABLE,
          path: [`${sourceVariable.value}`, InnerFieldValue.VALUE],
          subType: sourceVariable.type,
          dt: true,
          options: sourceVariable.options?.map((opt) => ({ value: opt, label: opt }))
        }] : []),
        {
          value: InnerFieldValue.LAST_MODIFICATION_DATE,
          label: i18next.t(`calculator.targetOption.sourceVariables.lastModificationDate`),
          type: FieldType.SOURCE_VARIABLE,
          fieldNature: TargetFieldNature.INDICATOR,
          path: [`${sourceVariable.value}`, InnerFieldValue.LAST_MODIFICATION_DATE],
          subType: FieldType.DATE,
          options: []
        }
      ])
    }
  });
}

export const getCriterionOperatorOptions = () => {
  return {
    TEXT: [
      {
        value: Operator.EQUAL,
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.CONTAINS,
        label: i18next.t("operator.contains"),
      },
      {
        value: Operator.STARTS_WITH,
        label: i18next.t("operator.startsWith"),
      },
      {
        value: Operator.ENDS_WITH,
        label: i18next.t("operator.endsWith"),
      },
      {
        value: Operator.GREATER_THAN,
        label: i18next.t("operator.greaterThan"),
      },
      {
        value: Operator.GREATER_OR_EQUAL,
        label: i18next.t("operator.greaterOrEqual"),
      },
      {
        value: Operator.LESS_THAN,
        label: i18next.t("operator.lessThan"),
      },
      {
        value: Operator.LESS_OR_EQUAL,
        label: i18next.t("operator.lessOrEqual"),
      },
      {
        value: Operator.IS_NULL,
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL,
        label: i18next.t("operator.notNull"),
      },
      {
        value: Operator.IN_SET,
        label: i18next.t("operator.inSet"),
      },
    ],

    NUMERIC: [
      {
        value: Operator.EQUAL,
        label: '=',
      },
      {
        value: Operator.NOT_EQUAL,
        label: '!=',
      },
      {
        value: Operator.GREATER_THAN,
        label: '>',
      },
      {
        value: Operator.GREATER_OR_EQUAL,
        label: '>=',
      },
      {
        value: Operator.LESS_THAN,
        label: '<',
      },
      {
        value: Operator.LESS_OR_EQUAL,
        label: '<=',
      },
      {
        value: Operator.IS_NULL,
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL,
        label: i18next.t("operator.notNull"),
      },
      {
        value: Operator.IN_SET,
        label: i18next.t("operator.inSet"),
      },
    ],

    DATE: [
      {
        value: Operator.EQUAL,
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.NOT_EQUAL,
        label: i18next.t("operator.notEqual"),
      },
      {
        value: Operator.GREATER_THAN,
        label: i18next.t("operator.date.greaterThan"),
      },
      {
        value: Operator.GREATER_OR_EQUAL,
        label: i18next.t("operator.date.greaterOrEqual"),
      },
      {
        value: Operator.LESS_THAN,
        label: i18next.t("operator.date.lessThan"),
      },
      {
        value: Operator.LESS_OR_EQUAL,
        label: i18next.t("operator.date.lessOrEqual"),
      },
      {
        value: Operator.IS_NULL,
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL,
        label: i18next.t("operator.notNull"),
      },
    ],

    BOOLEAN: [
      {
        value: Operator.EQUAL,
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.NOT_EQUAL,
        label: i18next.t("operator.notEqual"),
      },
      {
        value: Operator.IN_SET,
        label: i18next.t("operator.inSet"),
      },
    ],

    CUSTOM: [
      {
        value: Operator.EQUAL,
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.NOT_EQUAL,
        label: i18next.t("operator.notEqual"),
      },
      {
        value: Operator.IN_SET,
        label: i18next.t("operator.inSet"),
      },
    ],

    ATTRIBUTE: [
      {
        value: Operator.EQUAL,
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.CONTAINS,
        label: i18next.t("operator.contains"),
      },
      {
        value: Operator.STARTS_WITH,
        label: i18next.t("operator.startsWith"),
      },
      {
        value: Operator.ENDS_WITH,
        label: i18next.t("operator.endsWith"),
      },
      {
        value: Operator.GREATER_THAN,
        label: i18next.t("operator.greaterThan"),
      },
      {
        value: Operator.GREATER_OR_EQUAL,
        label: i18next.t("operator.greaterOrEqual"),
      },
      {
        value: Operator.LESS_THAN,
        label: i18next.t("operator.lessThan"),
      },
      {
        value: Operator.LESS_OR_EQUAL,
        label: i18next.t("operator.lessOrEqual"),
      },
      {
        value: Operator.IS_NULL,
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL,
        label: i18next.t("operator.notNull"),
      },
      {
        value: Operator.IN_SET,
        label: i18next.t("operator.inSet"),
      },
    ],

    SOURCE_DATA: [
      {
        value: Operator.EQUAL,
        label: 'Egal à',
      },
      {
        value: Operator.CONTAINS,
        label: 'Contient',
      },
      {
        value: Operator.STARTS_WITH,
        label: 'Commence par',
      },
      {
        value: Operator.ENDS_WITH,
        label: 'Se termine par',
      },
      {
        value: Operator.GREATER_THAN,
        label: 'Supérieur',
      },
      {
        value: Operator.GREATER_OR_EQUAL,
        label: 'Supérieur ou égal',
      },
      {
        value: Operator.LESS_THAN,
        label: 'Inférieur',
      },
      {
        value: Operator.LESS_OR_EQUAL,
        label: 'Inférieur ou égal',
      },
      {
        value: Operator.IS_NULL,
        label: 'Est vide',
      },
      {
        value: Operator.NOT_NULL,
        label: 'N\'est pas vide',
      },
      {
        value: Operator.IN_SET,
        label: 'Fait partie de',
      },
    ],

    RISK: [
      {
        value: Operator.EQUAL, // "IS"
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.NOT_EQUAL, //"NOT"
        label: i18next.t("operator.notEqual"),
      },
      {
        value: Operator.GREATER_THAN,
        label: i18next.t("operator.greaterThan"),
      },
      {
        value: Operator.GREATER_OR_EQUAL,
        label: i18next.t("operator.greaterOrEqual"),
      },
      {
        value: Operator.LESS_THAN,
        label: i18next.t("operator.lessThan"),
      },
      {
        value: Operator.LESS_OR_EQUAL,
        label: i18next.t("operator.lessOrEqual"),
      },
      {
        value: Operator.IN_SET, //"IS_ANY_OF"
        label: i18next.t("operator.inSet"),
      },
      // {
      //     value: Operator.NOT_IN_SET, //"IS_NOT_ANY_OF"
      //     label: "Ne fait pas partie de"
      // }
      {
        value: Operator.IS_NULL, //"IS_EMPTY"
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL,
        label: i18next.t("operator.notNull"),
      },
    ],

    CALCULATED_ATTRIBUTE: [
      {
        value: Operator.EQUAL,
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.CONTAINS,
        label: i18next.t("operator.contains"),
      },
      {
        value: Operator.STARTS_WITH,
        label: i18next.t("operator.startsWith"),
      },
      {
        value: Operator.ENDS_WITH,
        label: i18next.t("operator.endsWith"),
      },
      {
        value: Operator.GREATER_THAN,
        label: i18next.t("operator.greaterThan"),
      },
      {
        value: Operator.GREATER_OR_EQUAL,
        label: i18next.t("operator.greaterOrEqual"),
      },
      {
        value: Operator.LESS_THAN,
        label: i18next.t("operator.lessThan"),
      },
      {
        value: Operator.LESS_OR_EQUAL,
        label: i18next.t("operator.lessOrEqual"),
      },
      {
        value: Operator.IS_NULL,
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL,
        label: i18next.t("operator.notNull"),
      },
      {
        value: Operator.IN_SET,
        label: i18next.t("operator.inSet"),
      },
    ],

    INDICATOR: [
      {
        value: Operator.EQUAL,
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.CONTAINS,
        label: i18next.t("operator.contains"),
      },
      {
        value: Operator.STARTS_WITH,
        label: i18next.t("operator.startsWith"),
      },
      {
        value: Operator.ENDS_WITH,
        label: i18next.t("operator.endsWith"),
      },
      {
        value: Operator.GREATER_THAN,
        label: i18next.t("operator.greaterThan"),
      },
      {
        value: Operator.GREATER_OR_EQUAL,
        label: i18next.t("operator.greaterOrEqual"),
      },
      {
        value: Operator.LESS_THAN,
        label: i18next.t("operator.lessThan"),
      },
      {
        value: Operator.LESS_OR_EQUAL,
        label: i18next.t("operator.lessOrEqual"),
      },
      {
        value: Operator.IS_NULL,
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL,
        label: i18next.t("operator.notNull"),
      },
      {
        value: Operator.IN_SET,
        label: i18next.t("operator.inSet"),
      },
    ],

    SOURCE_VARIABLE: [
      {
        value: Operator.EQUAL,
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.CONTAINS,
        label: i18next.t("operator.contains"),
      },
      {
        value: Operator.STARTS_WITH,
        label: i18next.t("operator.startsWith"),
      },
      {
        value: Operator.ENDS_WITH,
        label: i18next.t("operator.endsWith"),
      },
      {
        value: Operator.GREATER_THAN,
        label: i18next.t("operator.greaterThan"),
      },
      {
        value: Operator.GREATER_OR_EQUAL,
        label: i18next.t("operator.greaterOrEqual"),
      },
      {
        value: Operator.LESS_THAN,
        label: i18next.t("operator.lessThan"),
      },
      {
        value: Operator.LESS_OR_EQUAL,
        label: i18next.t("operator.lessOrEqual"),
      },
      {
        value: Operator.IS_NULL,
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL,
        label: i18next.t("operator.notNull"),
      },
      {
        value: Operator.IN_SET,
        label: i18next.t("operator.inSet"),
      }
    ],

    FORM_FIELD: [
      {
        value: Operator.EQUAL,
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.CONTAINS,
        label: i18next.t("operator.contains"),
      },
      {
        value: Operator.STARTS_WITH,
        label: i18next.t("operator.startsWith"),
      },
      {
        value: Operator.ENDS_WITH,
        label: i18next.t("operator.endsWith"),
      },
      {
        value: Operator.GREATER_THAN,
        label: i18next.t("operator.greaterThan"),
      },
      {
        value: Operator.GREATER_OR_EQUAL,
        label: i18next.t("operator.greaterOrEqual"),
      },
      {
        value: Operator.LESS_THAN,
        label: i18next.t("operator.lessThan"),
      },
      {
        value: Operator.LESS_OR_EQUAL,
        label: i18next.t("operator.lessOrEqual"),
      },
      {
        value: Operator.IS_NULL,
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL,
        label: i18next.t("operator.notNull"),
      },
      {
        value: Operator.IN_SET,
        label: i18next.t("operator.inSet"),
      },
    ],

    ACTIVITY: [
      {
        value: Operator.EQUAL,
        label: i18next.t('operator.equal'),
      },
      {
        value: Operator.NOT_EQUAL,
        label: i18next.t('operator.notEqual'),
      },
      {
        value: Operator.IN_SET,
        label: i18next.t('operator.inSet'),
      },
    ],

    OCR_EXTRACTED_FIELD: [
      {
        value: Operator.EQUAL,
        label: i18next.t("operator.equal"),
      },
      {
        value: Operator.CONTAINS,
        label: i18next.t("operator.contains"),
      },
      {
        value: Operator.STARTS_WITH,
        label: i18next.t("operator.startsWith"),
      },
      {
        value: Operator.ENDS_WITH,
        label: i18next.t("operator.endsWith"),
      },
      {
        value: Operator.GREATER_THAN,
        label: i18next.t("operator.greaterThan"),
      },
      {
        value: Operator.GREATER_OR_EQUAL,
        label: i18next.t("operator.greaterOrEqual"),
      },
      {
        value: Operator.LESS_THAN,
        label: i18next.t("operator.lessThan"),
      },
      {
        value: Operator.LESS_OR_EQUAL,
        label: i18next.t("operator.lessOrEqual"),
      },
      {
        value: Operator.IS_NULL,
        label: i18next.t("operator.isNull"),
      },
      {
        value: Operator.NOT_NULL,
        label: i18next.t("operator.notNull"),
      },
      {
        value: Operator.IN_SET,
        label: i18next.t("operator.inSet"),
      },
    ]
  };
};

export const getAvailableOperatorOptions = (targetFieldEntry) => {
  const type = targetFieldEntry?.type;
  const subType = targetFieldEntry?.subType || ``;
  const complexListSubFieldType = targetFieldEntry?.complexListSubField?.type;

  let operatorOptions = [];
  if (
    type === FieldType.RISK &&
    ((subType === ThirdpartyScoreType.DATABLOCKS_ESG &&
      targetFieldEntry?.isNumeric === true)
      || subType === ThirdpartyScoreType.CREDITSAFE_NUMERIC)
  ) {
    operatorOptions = getCriterionOperatorOptions()[FieldType.NUMERIC];
  } else {
    operatorOptions = getCriterionOperatorOptions()[type];

    switch (type) {
      case FieldType.ATTRIBUTE:
        switch (subType) {
          case AttributeType.STRING:
          case AttributeType.LIST_UNIQUE:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.GREATER_THAN,
                  Operator.GREATER_OR_EQUAL,
                  Operator.GREATER_OR_EQUAL,
                  Operator.LESS_THAN,
                  Operator.LESS_OR_EQUAL,
                  Operator.BETWEEN,
                ].includes(entry.value),
            );

            break;

          case AttributeType.LIST_MULTIPLE:
            operatorOptions = operatorOptions.filter((entry) => ![
              Operator.STARTS_WITH,
              Operator.NOT_STARTS_WITH,
              Operator.ENDS_WITH,
              Operator.NOT_ENDS_WITH,
              Operator.GREATER_THAN,
              Operator.GREATER_OR_EQUAL,
              Operator.GREATER_OR_EQUAL,
              Operator.LESS_THAN,
              Operator.LESS_OR_EQUAL,
              Operator.BETWEEN,
              Operator.IN_SET,
              Operator.NOT_IN_SET,
            ].includes(entry.value)).map((entry) => {
              switch (entry.value) {
                case Operator.CONTAINS:
                  return {
                    value: entry.value,
                    label: i18next.t("operator.containsAtLeast"),
                  }

                default:
                  return entry;
              }
            });

            break;

          case AttributeType.DECIMAL:
          case AttributeType.INTEGER:
          case AttributeType.PERCENTAGE:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.CONTAINS,
                  Operator.NOT_CONTAINS,
                  Operator.STARTS_WITH,
                  Operator.NOT_STARTS_WITH,
                  Operator.ENDS_WITH,
                  Operator.NOT_ENDS_WITH,
                ].includes(entry.value),
            );

            break;

          case AttributeType.BOOLEAN:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.CONTAINS,
                  Operator.NOT_CONTAINS,
                  Operator.STARTS_WITH,
                  Operator.NOT_STARTS_WITH,
                  Operator.ENDS_WITH,
                  Operator.NOT_ENDS_WITH,
                  Operator.GREATER_THAN,
                  Operator.GREATER_OR_EQUAL,
                  Operator.GREATER_OR_EQUAL,
                  Operator.LESS_THAN,
                  Operator.LESS_OR_EQUAL,
                  Operator.BETWEEN,
                  Operator.IN_SET,
                  Operator.NOT_IN_SET,
                ].includes(entry.value),
            );

            break;

          case AttributeType.DATE:
          case FieldType.DATE:
            operatorOptions = operatorOptions
              .filter(
                (entry) =>
                  ![
                    Operator.CONTAINS,
                    Operator.NOT_CONTAINS,
                    Operator.STARTS_WITH,
                    Operator.NOT_STARTS_WITH,
                    Operator.ENDS_WITH,
                    Operator.NOT_ENDS_WITH,
                    Operator.BETWEEN, //TODO: Add these 3 later if asked for
                    Operator.IN_SET,
                    Operator.NOT_IN_SET,
                  ].includes(entry.value),
              )
              .map((entry) => formatDateOperatorLabel(entry));

            break;

          default:
            break;
        }
        break;

      case FieldType.CALCULATED_ATTRIBUTE:
      case FieldType.INDICATOR:
      case FieldType.SOURCE_VARIABLE:
        switch (subType) {
          case CalculationResultType.STRING:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.GREATER_THAN,
                  Operator.GREATER_OR_EQUAL,
                  Operator.GREATER_OR_EQUAL,
                  Operator.LESS_THAN,
                  Operator.LESS_OR_EQUAL,
                  Operator.BETWEEN,
                ].includes(entry.value),
            );

            break;

          case CalculationResultType.ARITHMETIC_FORMULA_OUTPUT:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.CONTAINS,
                  Operator.NOT_CONTAINS,
                  Operator.STARTS_WITH,
                  Operator.NOT_STARTS_WITH,
                  Operator.ENDS_WITH,
                  Operator.NOT_ENDS_WITH,
                ].includes(entry.value),
            );
            break;

          case CalculationResultType.BOOLEAN:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.CONTAINS,
                  Operator.NOT_CONTAINS,
                  Operator.STARTS_WITH,
                  Operator.NOT_STARTS_WITH,
                  Operator.ENDS_WITH,
                  Operator.NOT_ENDS_WITH,
                  Operator.GREATER_THAN,
                  Operator.GREATER_OR_EQUAL,
                  Operator.GREATER_OR_EQUAL,
                  Operator.LESS_THAN,
                  Operator.LESS_OR_EQUAL,
                  Operator.BETWEEN,
                  Operator.IN_SET,
                  Operator.NOT_IN_SET,
                ].includes(entry.value),
            );

            break;

          case FieldType.DATE:
            operatorOptions = operatorOptions
              .filter(
                (entry) =>
                  ![
                    Operator.CONTAINS,
                    Operator.NOT_CONTAINS,
                    Operator.STARTS_WITH,
                    Operator.NOT_STARTS_WITH,
                    Operator.ENDS_WITH,
                    Operator.NOT_ENDS_WITH,
                    Operator.BETWEEN, //TODO: Add these 3 later if asked for
                    Operator.IN_SET,
                    Operator.NOT_IN_SET,
                  ].includes(entry.value),
              )
              .map((entry) => formatDateOperatorLabel(entry));

            break;

          default:
            break;
        }
        break;

      case FieldType.FORM_FIELD:
        switch (subType) {
          case DocumentTypePropertyType.CHAINE:
          case DocumentTypePropertyType.MULTILIGNES:
          case DocumentTypePropertyType.EMAIL:
          case DocumentTypePropertyType.TELEPHONE:
          case DocumentTypePropertyType.IBAN:
          case DocumentTypePropertyType.RADIO_BUTTON:
          case DocumentTypePropertyType.LIST_ITEM:
          case DocumentTypePropertyType.BANK_COUNTRY_CODE:
            operatorOptions = operatorOptions.filter((entry) => ![
              Operator.GREATER_THAN,
              Operator.GREATER_OR_EQUAL,
              Operator.GREATER_OR_EQUAL,
              Operator.LESS_THAN,
              Operator.LESS_OR_EQUAL,
              Operator.BETWEEN,
            ].includes(entry.value));

            break;

          case DocumentTypePropertyType.MONTANT:
          case DocumentTypePropertyType.NOMBRE:
          case DocumentTypePropertyType.DEVISE:
          case DocumentTypePropertyType.DECIMAL:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.CONTAINS,
                  Operator.NOT_CONTAINS,
                  Operator.STARTS_WITH,
                  Operator.NOT_STARTS_WITH,
                  Operator.ENDS_WITH,
                  Operator.NOT_ENDS_WITH,
                ].includes(entry.value),
            );
            break;

          case DocumentTypePropertyType.DATE:
            operatorOptions = operatorOptions
              .filter(
                (entry) =>
                  ![
                    Operator.CONTAINS,
                    Operator.NOT_CONTAINS,
                    Operator.STARTS_WITH,
                    Operator.NOT_STARTS_WITH,
                    Operator.ENDS_WITH,
                    Operator.NOT_ENDS_WITH,
                    Operator.BETWEEN, //TODO: Add these 3 later if asked for
                    Operator.IN_SET,
                    Operator.NOT_IN_SET,
                  ].includes(entry.value),
              )
              .map((entry) => formatDateOperatorLabel(entry));

            break;

          case DocumentTypePropertyType.CASE_A_COCHER:
            operatorOptions = operatorOptions.filter((entry) => ![
              Operator.STARTS_WITH,
              Operator.NOT_STARTS_WITH,
              Operator.ENDS_WITH,
              Operator.NOT_ENDS_WITH,
              Operator.GREATER_THAN,
              Operator.GREATER_OR_EQUAL,
              Operator.GREATER_OR_EQUAL,
              Operator.LESS_THAN,
              Operator.LESS_OR_EQUAL,
              Operator.BETWEEN,
              Operator.IN_SET,
              Operator.NOT_IN_SET,
            ].includes(entry.value)).map((entry) => {
              switch (entry.value) {
                case Operator.CONTAINS:
                  return {
                    value: entry.value,
                    label: i18next.t("operator.containsAtLeast"),
                  }

                default:
                  return entry;
              }
            });
            break;

          case DocumentTypePropertyType.LISTE_CHOIX:
            switch (complexListSubFieldType) {
              case DocumentTypePropertyType.CHAINE:
              case DocumentTypePropertyType.MULTILIGNES:
              case DocumentTypePropertyType.EMAIL:
              case DocumentTypePropertyType.TELEPHONE:
              case DocumentTypePropertyType.IBAN:
              case DocumentTypePropertyType.RADIO_BUTTON:
              case DocumentTypePropertyType.LIST_ITEM:
              case DocumentTypePropertyType.BANK_COUNTRY_CODE:
                operatorOptions = operatorOptions.filter((entry) => ![
                  Operator.GREATER_THAN,
                  Operator.GREATER_OR_EQUAL,
                  Operator.GREATER_OR_EQUAL,
                  Operator.LESS_THAN,
                  Operator.LESS_OR_EQUAL,
                  Operator.BETWEEN,
                ].includes(entry.value));

                break;

              case DocumentTypePropertyType.MONTANT:
              case DocumentTypePropertyType.NOMBRE:
              case DocumentTypePropertyType.DEVISE:
              case DocumentTypePropertyType.DECIMAL:
                operatorOptions = operatorOptions.filter(
                  (entry) =>
                    ![
                      Operator.CONTAINS,
                      Operator.NOT_CONTAINS,
                      Operator.STARTS_WITH,
                      Operator.NOT_STARTS_WITH,
                      Operator.ENDS_WITH,
                      Operator.NOT_ENDS_WITH,
                    ].includes(entry.value),
                );
                break;

              case DocumentTypePropertyType.DATE:
                operatorOptions = operatorOptions
                  .filter(
                    (entry) =>
                      ![
                        Operator.CONTAINS,
                        Operator.NOT_CONTAINS,
                        Operator.STARTS_WITH,
                        Operator.NOT_STARTS_WITH,
                        Operator.ENDS_WITH,
                        Operator.NOT_ENDS_WITH,
                        Operator.BETWEEN, //TODO: Add these 3 later if asked for
                        Operator.IN_SET,
                        Operator.NOT_IN_SET,
                      ].includes(entry.value),
                  )
                  .map((entry) => formatDateOperatorLabel(entry));

                break;

              case DocumentTypePropertyType.CASE_A_COCHER:
                operatorOptions = operatorOptions.filter((entry) => ![
                  Operator.STARTS_WITH,
                  Operator.NOT_STARTS_WITH,
                  Operator.ENDS_WITH,
                  Operator.NOT_ENDS_WITH,
                  Operator.GREATER_THAN,
                  Operator.GREATER_OR_EQUAL,
                  Operator.GREATER_OR_EQUAL,
                  Operator.LESS_THAN,
                  Operator.LESS_OR_EQUAL,
                  Operator.BETWEEN,
                  Operator.IN_SET,
                  Operator.NOT_IN_SET,
                ].includes(entry.value)).map((entry) => {
                  switch (entry.value) {
                    case Operator.CONTAINS:
                      return {
                        value: entry.value,
                        label: i18next.t("operator.containsAtLeast"),
                      }

                      default:
                        return entry;
                    }
                  });
                break;

              default:
                break;
            }
            break;

          default:
            break;
        }
        break;

      case FieldType.SOURCE_DATA:
        switch (subType) {
          case FieldType.TEXT:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.GREATER_THAN,
                  Operator.GREATER_OR_EQUAL,
                  Operator.GREATER_OR_EQUAL,
                  Operator.LESS_THAN,
                  Operator.LESS_OR_EQUAL,
                  Operator.BETWEEN,
                ].includes(entry.value),
            );
            break;

          case FieldType.NUMERIC:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.CONTAINS,
                  Operator.NOT_CONTAINS,
                  Operator.STARTS_WITH,
                  Operator.NOT_STARTS_WITH,
                  Operator.ENDS_WITH,
                  Operator.NOT_ENDS_WITH,
                ].includes(entry.value),
            );
            break;

          case FieldType.BOOLEAN:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.CONTAINS,
                  Operator.NOT_CONTAINS,
                  Operator.STARTS_WITH,
                  Operator.NOT_STARTS_WITH,
                  Operator.ENDS_WITH,
                  Operator.NOT_ENDS_WITH,
                  Operator.GREATER_THAN,
                  Operator.GREATER_OR_EQUAL,
                  Operator.GREATER_OR_EQUAL,
                  Operator.LESS_THAN,
                  Operator.LESS_OR_EQUAL,
                  Operator.BETWEEN,
                  Operator.IN_SET,
                  Operator.NOT_IN_SET,
                ].includes(entry.value),
            );
            break;

          case FieldType.DATE:
            operatorOptions = operatorOptions
              .filter(
                (entry) =>
                  ![
                    Operator.CONTAINS,
                    Operator.NOT_CONTAINS,
                    Operator.STARTS_WITH,
                    Operator.NOT_STARTS_WITH,
                    Operator.ENDS_WITH,
                    Operator.NOT_ENDS_WITH,
                    Operator.BETWEEN, //TODO: Add these 3 later if asked for
                    Operator.IN_SET,
                    Operator.NOT_IN_SET,
                  ].includes(entry.value),
              )
              .map((entry) => formatDateOperatorLabel(entry));
            break;

          default:
            break;
        }
        break;
      case FieldType.CONTEXT_RANK:
        operatorOptions = getCriterionOperatorOptions()[
          FieldType.NUMERIC
        ].filter((entry) =>
          [
            Operator.EQUAL,
            Operator.GREATER_THAN,
            Operator.GREATER_OR_EQUAL,
            Operator.LESS_THAN,
            Operator.LESS_OR_EQUAL,
            Operator.BETWEEN,
          ].includes(entry.value),
        );
        break;

      case FieldType.TEXT:
        switch (subType) {
          case FieldType.DATE:
            operatorOptions = operatorOptions
              .filter(
                (entry) =>
                  ![
                    Operator.CONTAINS,
                    Operator.NOT_CONTAINS,
                    Operator.STARTS_WITH,
                    Operator.NOT_STARTS_WITH,
                    Operator.ENDS_WITH,
                    Operator.NOT_ENDS_WITH,
                    Operator.BETWEEN, //TODO: Add these 3 later if asked for
                    Operator.IN_SET,
                    Operator.NOT_IN_SET,
                  ].includes(entry.value),
              )
              .map((entry) => formatDateOperatorLabel(entry));
            break;

          default:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.GREATER_THAN,
                  Operator.GREATER_OR_EQUAL,
                  Operator.GREATER_OR_EQUAL,
                  Operator.LESS_THAN,
                  Operator.LESS_OR_EQUAL,
                  Operator.BETWEEN,
                ].includes(entry.value),
            );
            break;
        }
        break;
      case FieldType.RISK:
        switch (subType) {
          case FieldType.DATE:
            operatorOptions = operatorOptions
              .filter(
                (entry) =>
                  ![
                    Operator.CONTAINS,
                    Operator.NOT_CONTAINS,
                    Operator.STARTS_WITH,
                    Operator.NOT_STARTS_WITH,
                    Operator.ENDS_WITH,
                    Operator.NOT_ENDS_WITH,
                    Operator.BETWEEN, //TODO: Add these 3 later if asked for
                    Operator.IN_SET,
                    Operator.NOT_IN_SET,
                  ].includes(entry.value),
              )
              .map((entry) => formatDateOperatorLabel(entry));
            break;

          default:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.GREATER_THAN,
                  Operator.GREATER_OR_EQUAL,
                  Operator.GREATER_OR_EQUAL,
                  Operator.LESS_THAN,
                  Operator.LESS_OR_EQUAL,
                  Operator.BETWEEN,
                  Operator.NOT_NULL
                ].includes(entry.value),
            );
            break;
        }
        break;
      case FieldType.NUMERIC:
        switch (subType) {
          case FieldType.DATE:
            operatorOptions = operatorOptions
              .filter(
                (entry) =>
                  ![
                    Operator.CONTAINS,
                    Operator.NOT_CONTAINS,
                    Operator.STARTS_WITH,
                    Operator.NOT_STARTS_WITH,
                    Operator.ENDS_WITH,
                    Operator.NOT_ENDS_WITH,
                    Operator.BETWEEN, //TODO: Add these 3 later if asked for
                    Operator.IN_SET,
                    Operator.NOT_IN_SET,
                  ].includes(entry.value),
              )
              .map((entry) => formatDateOperatorLabel(entry));
            break;

          default:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.CONTAINS,
                  Operator.NOT_CONTAINS,
                  Operator.STARTS_WITH,
                  Operator.NOT_STARTS_WITH,
                  Operator.ENDS_WITH,
                  Operator.NOT_ENDS_WITH,
                ].includes(entry.value),
            );
            break;
        }
        break;

      case FieldType.OCR_EXTRACTED_FIELD:
        switch (subType) {
          case OcrExtractedFieldType.TEXT:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.GREATER_THAN,
                  Operator.GREATER_OR_EQUAL,
                  Operator.GREATER_OR_EQUAL,
                  Operator.LESS_THAN,
                  Operator.LESS_OR_EQUAL,
                  Operator.BETWEEN,
                ].includes(entry.value),
            );
            break;

          case OcrExtractedFieldType.NUMERIC:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.CONTAINS,
                  Operator.NOT_CONTAINS,
                  Operator.STARTS_WITH,
                  Operator.NOT_STARTS_WITH,
                  Operator.ENDS_WITH,
                  Operator.NOT_ENDS_WITH,
                ].includes(entry.value),
            );
            break;

          case OcrExtractedFieldType.DATE:
            operatorOptions = operatorOptions
              .filter(
                (entry) =>
                  ![
                    Operator.CONTAINS,
                    Operator.NOT_CONTAINS,
                    Operator.STARTS_WITH,
                    Operator.NOT_STARTS_WITH,
                    Operator.ENDS_WITH,
                    Operator.NOT_ENDS_WITH,
                    Operator.BETWEEN, //TODO: Add these 3 later if asked for
                    Operator.IN_SET,
                    Operator.NOT_IN_SET,
                  ].includes(entry.value),
              )
              .map((entry) => formatDateOperatorLabel(entry));

            break;

          case OcrExtractedFieldType.BOOLEAN:
          case FieldType.BOOLEAN:
            operatorOptions = operatorOptions.filter(
              (entry) =>
                ![
                  Operator.CONTAINS,
                  Operator.NOT_CONTAINS,
                  Operator.STARTS_WITH,
                  Operator.NOT_STARTS_WITH,
                  Operator.ENDS_WITH,
                  Operator.NOT_ENDS_WITH,
                  Operator.GREATER_THAN,
                  Operator.GREATER_OR_EQUAL,
                  Operator.GREATER_OR_EQUAL,
                  Operator.LESS_THAN,
                  Operator.LESS_OR_EQUAL,
                  Operator.BETWEEN,
                  Operator.IN_SET,
                  Operator.NOT_IN_SET,
                ].includes(entry.value),
            );
            break;

          default:
            break;
        }
        break;
      default:
        break;
    }
  }

  return operatorOptions;
};

const formatDateOperatorLabel = (attributeOp) => {
  let operator = {
    value: attributeOp.value,
  };
  switch (attributeOp.value) {
    case Operator.EQUAL:
      operator.label = i18next.t('operator.equal');
      break;
    case Operator.NOT_EQUAL:
      operator.label = i18next.t('operator.notEqual');
      break;
    case Operator.GREATER_THAN:
      operator.label = i18next.t('operator.date.greaterThan');
      break;
    case Operator.GREATER_OR_EQUAL:
      operator.label = i18next.t('operator.date.greaterOrEqual');
      break;
    case Operator.LESS_THAN:
      operator.label = i18next.t('operator.date.lessThan');
      break;
    case Operator.LESS_OR_EQUAL:
      operator.label = i18next.t('operator.date.lessOrEqual');
      break;
    case Operator.IS_NULL:
      operator.label = i18next.t('operator.isNull');
      break;
    case Operator.NOT_NULL:
      operator.label = i18next.t('operator.notNull');
      break;
    default:
      operator.label = attributeOp.label; // Make sure this handles localization as needed
      break;
  }

  return operator;
};

export const parseTargetFieldCategoryFromVariable = (variable) => {
  return variable?.targetField?.target_field_category;
};

export const parseDossierTypeIdFromVariable = (variable) => {
  const targetFieldCategory = variable?.targetField?.target_field_category;
  let dossierTypeId = undefined;

  switch (targetFieldCategory) {
    case TargetFieldCategory.DOSSIER:
      dossierTypeId =
        variable?.targetField?.target_field_data?.data_for_dossier
          ?.dossier_type_id;
      break;

    default:
      break;
  }

  return dossierTypeId;
};

export const parseDocumentTypeIdFromVariable = (variable) => {
  const targetFieldCategory = variable?.targetField?.target_field_category;
  let documentTypeId = undefined;

  switch (targetFieldCategory) {
    case TargetFieldCategory.DOCUMENT:
      documentTypeId =
        variable?.targetField?.target_field_data?.data_for_document
          ?.document_type_id;
      break;

    default:
      break;
  }

  return documentTypeId;
};

export const parseTargetFieldTypeFromVariable = (variable) => {
  const targetFieldCategory = variable?.targetField?.target_field_category;
  let targetFieldType = undefined;

  switch (targetFieldCategory) {
    case TargetFieldCategory.THIRD_PARTY:
      targetFieldType =
        variable?.targetField?.target_field_data?.data_for_third_party
          ?.field_type;
      break;

    case TargetFieldCategory.DOSSIER:
      targetFieldType =
        variable?.targetField?.target_field_data?.data_for_dossier?.field_type;
      break;

    case TargetFieldCategory.DOCUMENT:
      targetFieldType =
        variable?.targetField?.target_field_data?.data_for_document?.field_type;
      break;

    case TargetFieldCategory.SOURCE_VARIABLE:
      targetFieldType =
        variable?.targetField?.target_field_data?.data_for_source_variable
          ?.field_type;
      break;
    case TargetFieldCategory.CONTEXT:
      targetFieldType =
        variable?.targetField?.target_field_data?.data_for_context?.field_type;
      break;
    default:
      return targetFieldType;
  }

  return targetFieldType;
};

export const parseTargetFieldFromVariable = (variable) => {
  const targetFieldCategory = variable?.targetField?.target_field_category;
  let targetField = undefined;
  switch (targetFieldCategory) {
    case TargetFieldCategory.THIRD_PARTY:
      targetField =
        variable?.targetField?.target_field_data?.data_for_third_party?.field;
      break;

    case TargetFieldCategory.DOSSIER:
      targetField =
        variable?.targetField?.target_field_data?.data_for_dossier?.field;
      break;

    case TargetFieldCategory.DOCUMENT:
      targetField =
        variable?.targetField?.target_field_data?.data_for_document?.field;
      break;

    case TargetFieldCategory.SOURCE_VARIABLE:
      targetField =
        variable?.targetField?.target_field_data?.data_for_source_variable
          ?.field;
      break;
    case TargetFieldCategory.CONTEXT:
      targetField =
        variable?.targetField?.target_field_data?.data_for_context?.field;
      break;

    default:
      break;
  }

  return targetField;
}

export const parseTargetInnerFieldFromVariable = (variable) => {
  const targetFieldCategory = variable?.targetField?.target_field_category;
  let targetField = undefined;

  switch (targetFieldCategory) {
    case TargetFieldCategory.THIRD_PARTY:
      targetField = variable?.targetField?.target_field_data?.data_for_third_party?.inner_field;
      break;

    case TargetFieldCategory.DOSSIER:
      targetField = variable?.targetField?.target_field_data?.data_for_dossier?.inner_field;
      break;

    case TargetFieldCategory.DOCUMENT:
      targetField = variable?.targetField?.target_field_data?.data_for_document?.inner_field;
      break;

    case TargetFieldCategory.SOURCE_VARIABLE:
      targetField = variable?.targetField?.target_field_data?.data_for_source_variable?.inner_field;
      break;

    case TargetFieldCategory.CONTEXT:
      targetField = variable?.targetField?.target_field_data?.data_for_context?.inner_field;
      break;
    default:
      break;
  }

  return targetField;
};

export const parseTargetComplexListSubFieldFromVariable = (variable) => {
  const targetFieldCategory = variable?.targetField?.target_field_category;
  let complexListSubField = undefined;

  switch (targetFieldCategory) {
    case TargetFieldCategory.DOCUMENT:
      complexListSubField = variable?.targetField?.target_field_data?.data_for_document?.complex_list_sub_field;
      break;

    default:
      break;
  }

  return complexListSubField;
};

export const isValidBranch = (branch) => {
  let bValid = false;

  if (branch?.default) {
    bValid = branch?.resultValue !== '';
  } else {
    bValid =
      branch?.resultValue !== '' &&
      !_.isEmpty(branch?.flatCriteria) &&
      _.every(branch?.flatCriteria, isValidCriterion) &&
      checkLogicalExpression(branch?.formula, branch?.flatCriteria) === ``;
  }

  return bValid;
};

export const isValidCriteriaGroup = (group) => {
  return (
    (!_.isEmpty(group?.criteria) &&
      _.every(group?.criteria, isValidCriterion)) ||
    (!_.isEmpty(group?.childGroups) &&
      _.every(group?.criteriaGroups, isValidCriteriaGroup))
  );
};

export const isValidCriterion = (criterion) => {
  return (
    criterion &&
    criterion.variableName !== '' &&
    criterion.operator !== '' &&
    ([Operator.IS_NULL, Operator.NOT_NULL].includes(criterion.operator) ||
      !_.isEmpty(criterion.values))
  );
};

export const cleanFormData = (data) => {
  let cleanData = JSON.parse(JSON.stringify(data));

  cleanData.variables.forEach((variable) => cleanVariableData(variable));
  cleanData.branches?.forEach((branch) => cleanBranchData(branch));

  return cleanData;
};

export const cleanVariableData = (variable) => {
  if (`${variable.id}`.indexOf('tmp') !== -1) {
    variable.id = null;
  }
};

export const cleanBranchData = (branch) => {
  if (`${branch.id}`.indexOf('tmp') !== -1) {
    branch.id = null;
  }

  branch.connectiveOperator = ConnectiveOperatorType.AND;

  if (branch?.default === false) {
    const data = parseLogicalExpression(branch.formula, branch.flatCriteria);
    branch.criteriaGroups = [data];
    branch.flatCriteria = [];
  } else {
    branch.criteriaGroups = [];
    branch.flatCriteria = [];
  }

  branch.criteriaGroups?.forEach((group) => cleanCriteriaGroupData(group));
};

export const cleanCriteriaGroupData = (group) => {
  if (`${group.id}`.indexOf('tmp') !== -1) {
    group.id = null;
  }

  group.criteria?.forEach((criterion) => cleanCriterionData(criterion));
  group.childGroups?.forEach((childGroup) =>
    cleanCriteriaGroupData(childGroup),
  );
};

export const cleanCriterionData = (criterion) => {
  // if (`${criterion.id}`.indexOf("tmp") !== -1) {
  criterion.id = null;
  criterion.groupId = null;
  // }

  if (`${criterion.variableId}`.indexOf('tmp') !== -1) {
    criterion.variableId = null;
  }
};

export const parseLogicalExpression = (expr, criteria) => {
  jsep.addBinaryOp(ConnectiveOperatorType.OR, 1);
  jsep.addBinaryOp(ConnectiveOperatorType.OR.toLowerCase(), 1);
  jsep.addBinaryOp(ConnectiveOperatorType.AND, 2);
  jsep.addBinaryOp(ConnectiveOperatorType.AND.toLowerCase(), 2);

  const tree = jsep(expr);
  return parseLogicalTree(tree, criteria);
};

export const parseLogicalTree = (tree, criteria) => {
  switch (getNodeType(tree)) {
    case CalculationBranchFormulaNodeType.CRITERION:
      return {
        id: null,
        connectiveOperator: ConnectiveOperatorType.AND.toUpperCase(),
        criteria: [
          {
            ...criteria[tree.value - 1],
            id: null,
            groupId: null,
          },
        ],
        childGroups: [],
        parentGroupId: null,
        branchId: null,
      };

    case CalculationBranchFormulaNodeType.GROUP:
      let group = {
        id: null,
        connectiveOperator: tree.operator.toUpperCase(),
        criteria: [],
        childGroups: [],
        parentGroupId: null,
        branchId: null,
      };

      const left = parseLogicalTree(tree.left, criteria);
      const right = parseLogicalTree(tree.right, criteria);

      if (_.size(left?.criteria) === 1 && _.isEmpty(left.childGroups)) {
        group.criteria.push(...left?.criteria);
      } else if (_.size(left?.childGroups) === 1 && _.isEmpty(left.criteria)) {
        group.childGroups.push(...left?.childGroups);
      } else {
        group.childGroups.push(left);
      }

      if (_.size(right?.criteria) === 1 && _.isEmpty(right.childGroups)) {
        group.criteria.push(...right?.criteria);
      } else if (
        _.size(right?.childGroups) === 1 &&
        _.isEmpty(right.criteria)
      ) {
        group.childGroups.push(...right?.childGroups);
      } else {
        group.childGroups.push(right);
      }

      return group;

    default:
      throw new Error('Unsupported node type');
  }
};

const getNodeType = (node) => {
  switch (node.type) {
    case 'Literal':
      return CalculationBranchFormulaNodeType.CRITERION;

    case 'BinaryExpression':
      return CalculationBranchFormulaNodeType.GROUP;

    case 'Identifier':
    case 'CallExpression':
    case 'UnaryExpression':
    case 'Compound':
    default:
      throw new Error('Unsupported node type');
  }
};

export const checkLogicalExpression = (expr, criteria) => {
  let message = ``;

  jsep.addBinaryOp(ConnectiveOperatorType.OR, 1);
  jsep.addBinaryOp(ConnectiveOperatorType.OR.toLowerCase(), 1);
  jsep.addBinaryOp(ConnectiveOperatorType.AND, 2);
  jsep.addBinaryOp(ConnectiveOperatorType.AND.toLowerCase(), 2);

  try {
    const involvedLiterals = exctractTreeLiterals(jsep(expr));

    if (!_.isEmpty(criteria)) {
      if (_.isEmpty(involvedLiterals)) {
        message = `missingLiteralInFormula`;
      } else {
        const expectedLiterals = _.range(1, _.size(criteria) + 1);

        if (_.size(_.difference(involvedLiterals, expectedLiterals)) > 0) {
          message = `unexpectedLiteralInFormula`;
        } else if (
          _.size(_.difference(expectedLiterals, involvedLiterals)) > 0
        ) {
          message = `missingLiteralInFormula`;
        } else if (!_.isEqual(expectedLiterals, involvedLiterals)) {
          return `wrongLiteralsOrderInFormula`;
        }
      }
    } else if (!_.isEmpty(involvedLiterals)) {
      message = `unexpectedLiteralInFormula`;
    }
  } catch (error) {
    const errorMessage = error?.message;

    if (errorMessage?.startsWith('Unclosed')) {
      message = `unclosedParenthese`;
    } else if (errorMessage?.startsWith('Unexpected')) {
      message = `unexpectedParenthese`;
    } else if (errorMessage?.startsWith('Unsupported')) {
      message = `unsupportedOperator`;
    } else if (errorMessage?.startsWith('Expected')) {
      message = `missingLiteralInFormula`;
    } else {
      message = `badFormula`;
    }
  }

  return message;
};

// export const checkTree = (tree) => {
//   let bAccept = false;

//   switch (tree.type) {
//     case 'Literal':
//       bAccept = true;
//       break;

//     case 'BinaryExpression':
//       if ([
//         ConnectiveOperatorType.AND,
//         ConnectiveOperatorType.AND.toLocaleLowerCase(),
//         ConnectiveOperatorType.OR,
//         ConnectiveOperatorType.OR.toLocaleLowerCase(),
//       ].includes(tree.operator)) {
//         bAccept = checkTree(tree.left) && checkTree(tree.right);
//       } else {
//         bAccept = false;
//       }
//       break;

//     case 'Identifier':
//     case 'CallExpression':
//     case 'UnaryExpression':
//     case 'Compound':
//     default:
//       bAccept = false;
//       break;
//   }

//   return bAccept;
// }

export const exctractTreeLiterals = (tree) => {
  let literals = [];

  switch (tree.type) {
    case 'Literal':
      literals.push(tree.value);
      break;

    case 'BinaryExpression':
      if (
        ![
          ConnectiveOperatorType.AND,
          ConnectiveOperatorType.AND.toLocaleLowerCase(),
          ConnectiveOperatorType.OR,
          ConnectiveOperatorType.OR.toLocaleLowerCase(),
        ].includes(tree.operator)
      ) {
        throw new Error('Unsupported node type');
      }
      exctractTreeLiterals(tree.left)?.forEach((literal) =>
        literals.push(literal),
      );
      exctractTreeLiterals(tree.right)?.forEach((literal) =>
        literals.push(literal),
      );
      break;

    case 'Identifier':
    case 'CallExpression':
    case 'UnaryExpression':
    case 'Compound':
    default:
      throw new Error('Unsupported node type');
  }

  return literals;
};

export const checkArithmeticExpression = (expr, authorizedArguments) => {
  let message = ``;

  try {
    const involvedIdentifiers = extractTreeIdentifiers(jsep(expr));

    if (_.size(_.difference(involvedIdentifiers, [...authorizedArguments, CALCULATION_TODAY_VARIABLE_NAME])) > 0) {
      message = `unexpectedIdentifierInFormula`;
    }
  } catch (error) {
    const errorMessage = error?.message;

    if (errorMessage !== undefined && errorMessage !== '') {
      if (errorMessage.startsWith('Unclosed')) {
        message = `unclosedParenthese`;
      } else if (errorMessage.startsWith('Unexpected')) {
        message = `unexpectedParenthese`;
      } else if (errorMessage.startsWith('Unsupported')) {
        message = `unsupportedArithmeticOperator`;
      } else {
        message = `badFormula`;
      }
    } else {
      message = `badFormula`;
    }
  }

  return message;
};

export const extractTreeIdentifiers = (tree) => {
  let identifiers = [];

  switch (tree.type) {
    case 'Literal':
      // Literals are authorized, nothing to do here
      break;

    case 'BinaryExpression':
      if (
        ![
          ArithmeticOperatorType.ADDITION,
          ArithmeticOperatorType.SUBSTRACTION,
          ArithmeticOperatorType.MULTIPLICATION,
          ArithmeticOperatorType.DIVISION,
          // ArithmeticOperatorType.MODULO,
        ].includes(tree.operator)
      ) {
        throw new Error('Unsupported node type');
      }
      extractTreeIdentifiers(tree.left)?.forEach(identifier => identifiers.push(identifier));
      extractTreeIdentifiers(tree.right)?.forEach(identifier => identifiers.push(identifier));
      break;

    case 'Identifier':
      identifiers.push(tree.name);
      break;

    case 'CallExpression':
    case 'UnaryExpression':
    case 'Compound':
    default:
      throw new Error('Unsupported node type');
  }

  return identifiers;
};

export const getFlatCriteriaFromBranch = (branch) => {
  let flatCriteria = new Set();

  branch?.criteriaGroups?.forEach((group) => {
    getFlatCriteriaFromCriteriaGroup(group)?.forEach((criterion) =>
      flatCriteria.add(criterion),
    );
  });

  return Array.from(flatCriteria);
};

export const getFlatCriteriaFromCriteriaGroup = (group) => {
  let flatCriteria = new Set();

  group?.criteria?.forEach((criterion) => flatCriteria.add(criterion));
  group?.childGroups?.forEach((group) => {
    getFlatCriteriaFromCriteriaGroup(group)?.forEach((criterion) =>
      flatCriteria.add(criterion),
    );
  });

  return Array.from(flatCriteria);
};

export const isCalculationReferencedByTargetCalculation = (
  calculation,
  targetCalculation,
  otherCalculations,
) => {
  let bReferenced = false;

  if (calculation && targetCalculation) {
    if (calculation.id === targetCalculation.id) {
      bReferenced = true;
    } else {
      for (const variable of targetCalculation.variables) {
        let bTargetted = false;
        const targetFieldType = parseTargetFieldTypeFromVariable(variable);
        const targetField = parseTargetFieldFromVariable(variable);

        if (
          `${targetFieldType}` === `${calculation.nature}` &&
          parseInt(targetField) === calculation.id
        ) {
          bTargetted = true;
        } else if (
          [
            CalculationNature.CALCULATED_ATTRIBUTE,
            CalculationNature.INDICATOR,
            CalculationNature.SOURCE_VARIABLE,
          ].includes(targetFieldType)
        ) {
          if (!_.isEmpty(otherCalculations)) {
            const newTargetCalculation = _.find(otherCalculations, {
              id: parseInt(targetField),
            });

            if (newTargetCalculation) {
              const newOtherCalculations = otherCalculations?.filter(
                (entry) =>
                  entry.id !== calculation?.id &&
                  entry.id !== parseInt(targetField),
              );

              bTargetted = isCalculationReferencedByTargetCalculation(
                calculation,
                newTargetCalculation,
                newOtherCalculations,
              );
            }
          }
        }

        if (bTargetted) {
          bReferenced = true;
          break;
        }
      }
    }
  }

  return bReferenced;
};

export const getGridColumnTypeForCalculationResult = (resultType) => {
  switch (resultType) {
    case CalculationResultType.STRING:
      return 'string';

    case CalculationResultType.ARITHMETIC_FORMULA_OUTPUT:
      return 'number';

    case CalculationResultType.BOOLEAN:
      return 'singleSelect';

    default:
      return 'string';
  }
};

export const buildGridColumnsForCalculations = (calculations) => {
  return calculations
    ?.map((calc) => ({
      calculationId: calc.id,
      label: calc.name,
      type: calc.resultType,
      gridColumnType: getGridColumnTypeForCalculationResult(calc.resultType),
      values: calc.branches?.map((branch) => branch.resultValue)?.sort() || [],
    }))
    ?.sort((a, b) => a.label?.localeCompare(b.label));
};

/**
 * Return formated string for calculated attributes or indicator
 * @param item (indicator or calculated attribute)
 * @returns {string|*}
 */
export const formatCalculationResult = (item) => {
  switch (item.resultType) {
    case CalculationResultType.STRING:
      return (
        item.resultValue || CALCULATION_DISPLAY_LABEL_FOR_NULL_RESULT_VALUE()
      );

    case CalculationResultType.ARITHMETIC_FORMULA_OUTPUT:
      return item.resultValue
        ? formatDouble(item.resultValue)
        : CALCULATION_DISPLAY_LABEL_FOR_NULL_RESULT_VALUE();

    case CalculationResultType.BOOLEAN:
      return item.resultValue === 'true' ? true : false;

    default:
      return;
  }
};

export const formatDouble = (inputValue) => {
  let value = parseFloat(inputValue);

  if (isNaN(value)) {
    throw new Error('Invalid number');
  }

  return Math.floor(value) === value ? value.toFixed(0) : value.toString();
};

export const getOrderedOptions = (options) => {
  return options ? options.sort((a, b) => a.label.localeCompare(b.label)) : [];
};

export const hasInnerField = (entry) => {
  const typesWithInnerField = new Set([
    FieldType.ATTRIBUTE,
    FieldType.CALCULATED_ATTRIBUTE,
    FieldType.INDICATOR,
    FieldType.SOURCE_VARIABLE,
    FieldType.NUMERIC,
    FieldType.RISK,
    FieldType.TEXT,
  ]);

  if (!entry?.type) return false;
  return typesWithInnerField.has(entry.type) &&
    (entry.value === InnerFieldValue.VALUE || entry.value === InnerFieldValue.LAST_MODIFICATION_DATE);
};

export const isMatchingThirdPartyTarget = (item) => item?.targetResourceType === CalculationTargetResourceType.THIRD_PARTY;

export const isMatchingContextTarget = (item, contextModelId) => {
  return (
    item?.targetResourceType === CalculationTargetResourceType.CONTEXT &&
    _.some(item?.targetResourceKeys, key => contextModelId !== undefined && key === Number(contextModelId))
  );
};

export const isMatchingDossierTarget = (item, dossierTypeId) => {
  if (item?.targetResourceType !== CalculationTargetResourceType.DOSSIER) return false;

  const normalize = v => (v == null || v === 'null' ? null : String(v));
  const id = normalize(dossierTypeId);

  return _.some(item.targetResourceKeys, key => {
    const k = normalize(key);
    return (
      (k === null && id === null) ||
      k === id ||
      Number(k) === Number(id)
    );
  });
};

export const isComplexListSubField = (entry) => {
  return (entry.subType === DocumentTypePropertyType.LISTE_CHOIX) &&
          (entry.complexListSubField !== undefined);
};
